import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Send, ArrowDownToLine, ShoppingCart, LogOut, Trophy, Search, QrCode, Share2, Download, MessageCircle, Settings, RefreshCw, X, Users, Calendar, MapPin, Package, Gift, Database } from 'lucide-react';
import AdminBackup from '@/pages/AdminBackup';
import { QRCodeSVG } from 'qrcode.react';
import QRScanner from '@/components/QRScanner';
import { registerServiceWorker, requestNotificationPermission, showNotification, testNotification, checkNotificationPermission } from '@/utils/notifications';
import LevelBadge, { ExpProgressBar } from '@/components/LevelBadge';
import InteractiveMap from '@/components/InteractiveMap';
import { compressProfilePicture, compressBannerImage, compressImageToBase64 } from '@/utils/imageCompression';

const Dashboard = ({ user, onLogout, onUpdateUser, onOpenMessages, onOpenTournament, onOpenCommunityPosts, onOpenEvents, onOpenMap, onOpenBattle, unreadCount, setUnreadCount, onOpenStore }) => {
  const [leaderboard, setLeaderboard] = useState(() => {
    // Initialize from localStorage cache to keep mounted while logged in
    const cached = localStorage.getItem('leaderboardCache');
    return cached ? JSON.parse(cached) : [];
  });
  const [friendsOnly, setFriendsOnly] = useState(false);
  const [fetchingLeaderboard, setFetchingLeaderboard] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [sendUsername, setSendUsername] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [sendPoints, setSendPoints] = useState('');
  const [loading, setLoading] = useState(false);
  const [checkingPayment, setCheckingPayment] = useState(false);
  const [failedSessionId, setFailedSessionId] = useState(null);
  const [showManualComplete, setShowManualComplete] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [messagesEnabled, setMessagesEnabled] = useState(true);
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [friendsOnlyMessages, setFriendsOnlyMessages] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [showNotificationBlockedModal, setShowNotificationBlockedModal] = useState(false);
  const [showDeviceBackgroundModal, setShowDeviceBackgroundModal] = useState(false);
  const [uploadingDeviceBackground, setUploadingDeviceBackground] = useState(false);
  const [showOuterDeviceBackgroundModal, setShowOuterDeviceBackgroundModal] = useState(false);
  const [uploadingOuterDeviceBackground, setUploadingOuterDeviceBackground] = useState(false);
  const [showLeaderboardBackgroundModal, setShowLeaderboardBackgroundModal] = useState(false);
  const [uploadingLeaderboardBackground, setUploadingLeaderboardBackground] = useState(false);
  const [showBannerModal, setShowBannerModal] = useState(false);
  const [uploadingBanner, setUploadingBanner] = useState(false);
  const [uploadingCircleColor, setUploadingCircleColor] = useState(false);
  const [hasNewCommunityPosts, setHasNewCommunityPosts] = useState(false);
  const [notification, setNotification] = useState(null);
  const [onlineCount, setOnlineCount] = useState(0);
  const [showOnlineUsersModal, setShowOnlineUsersModal] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [onlineUserSearchQuery, setOnlineUserSearchQuery] = useState('');
  const [showBattlePopup, setShowBattlePopup] = useState(false); // Show battle popup on login after delay
  const [wallpaperLoaded, setWallpaperLoaded] = useState(false);
  const popupDismissedRef = useRef(false); // Track if user dismissed popup (ref to persist across re-renders)
  const [showAdminBackup, setShowAdminBackup] = useState(false); // Admin backup modal
  const [accountStatus, setAccountStatus] = useState(() => {
    // Initialize from localStorage to prevent flash of "Unverified" for verified users
    const cached = localStorage.getItem('accountStatus');
    return cached ? JSON.parse(cached) : null;
  }); // Account status (temporary/permanent)
  const [showVerifyAccountModal, setShowVerifyAccountModal] = useState(false); // Verify account popup
  const [showDeleteAccountModal, setShowDeleteAccountModal] = useState(false); // Delete account confirmation modal
  const [deleteHoldProgress, setDeleteHoldProgress] = useState(0); // 0-100 progress for hold button
  const [deletePassword, setDeletePassword] = useState(''); // Password for delete confirmation
  const [deletingAccount, setDeletingAccount] = useState(false); // Loading state for deletion
  const deleteHoldTimerRef = useRef(null); // Timer for hold progress
  const deleteHoldStartRef = useRef(null); // Start time of hold
  const notificationAudioRef = useRef(null);
  const wsRef = useRef(null);
  const communityWsRef = useRef(null);
  const friendsOnlyRef = useRef(friendsOnly);
  
  // Swipe detection refs for modals
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const lastSwipeTime = useRef(0);

  // Update ref whenever friendsOnly changes
  useEffect(() => {
    friendsOnlyRef.current = friendsOnly;
    fetchLeaderboard();
  }, [friendsOnly]);

  // Close popup on scroll - immediately
  useEffect(() => {
    if (!showBattlePopup) return;

    const handleScrollOrWheel = () => {
      // Close popup instantly on any scroll/wheel movement
      setShowBattlePopup(false);
      popupDismissedRef.current = true;
    };

    // Add event listeners for immediate response
    window.addEventListener('scroll', handleScrollOrWheel, { capture: true, passive: true });
    window.addEventListener('wheel', handleScrollOrWheel, { capture: true, passive: true });
    window.addEventListener('touchmove', handleScrollOrWheel, { capture: true, passive: true });

    return () => {
      window.removeEventListener('scroll', handleScrollOrWheel, { capture: true, passive: true });
      window.removeEventListener('wheel', handleScrollOrWheel, { capture: true, passive: true });
      window.removeEventListener('touchmove', handleScrollOrWheel, { capture: true, passive: true });
    };
  }, [showBattlePopup]);

  // Preload wallpaper image immediately on mount, then show popup when ready
  useEffect(() => {
    // Don't show popup again if user already dismissed it
    if (popupDismissedRef.current) return;
    
    const wallpaperUrl = user?.battle_stadium_background || 'https://customer-assets.emergentagent.com/job_0ddfa8dd-f7f0-44b2-a1dd-341ae5dc7366/artifacts/6tz3u9fb_IMG_1461.jpeg';
    
    const img = new Image();
    img.onload = () => {
      setWallpaperLoaded(true);
      if (!popupDismissedRef.current) {
        setShowBattlePopup(true);
      }
    };
    img.onerror = () => {
      // Even if image fails, show popup after a timeout
      setWallpaperLoaded(true);
      if (!popupDismissedRef.current) {
        setShowBattlePopup(true);
      }
    };
    img.src = wallpaperUrl;
    
    // Fallback timeout in case image takes too long
    const timeout = setTimeout(() => {
      if (!wallpaperLoaded && !popupDismissedRef.current) {
        setWallpaperLoaded(true);
        setShowBattlePopup(true);
      }
    }, 5000);
    
    return () => clearTimeout(timeout);
  }, []);

  useEffect(() => {
    // Wait for user to be authenticated before loading data
    if (!user?.id) {
      console.log('⏳ Waiting for user authentication...');
      return;
    }
    
    console.log('✅ User authenticated, loading dashboard data...');
    
    // Set initial state from user (synchronous - fastest)
    setMessagesEnabled(!user.messages_disabled);
    setNotificationsEnabled(user.notifications_enabled !== false);
    setFriendsOnlyMessages(user.messages_from_friends_only === true);
    
    // Fetch account status (temporary/permanent)
    const fetchAccountStatus = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/account/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAccountStatus(response.data);
        // Cache account status to prevent flash of "Unverified" on page navigation
        localStorage.setItem('accountStatus', JSON.stringify(response.data));
        
        // If account was deleted (expired), log out the user
        if (response.data.account_deleted) {
          alert('Your temporary account has expired and been deleted. You can create a new account with the same username if desired.');
          localStorage.removeItem('token');
          localStorage.removeItem('accountStatus');
          onLogout();
        }
      } catch (error) {
        // If we get a 401/404 error, the account may have been deleted
        if (error.response && (error.response.status === 401 || error.response.status === 404)) {
          localStorage.removeItem('token');
          localStorage.removeItem('accountStatus');
          onLogout();
        }
        console.error('Error fetching account status:', error);
      }
    };
    fetchAccountStatus();
    
    // Update account status timer - every minute normally, every 10 seconds when near expiry
    const statusInterval = setInterval(fetchAccountStatus, 60000);
    
    // Also set up a faster check when timer is low (under 5 minutes)
    const fastCheckInterval = setInterval(() => {
      if (accountStatus && !accountStatus.is_permanent && accountStatus.time_remaining_seconds !== null) {
        if (accountStatus.time_remaining_seconds <= 300 && accountStatus.time_remaining_seconds > 0) {
          fetchAccountStatus();
        }
      }
    }, 10000);
    
    // Load all critical data immediately and in parallel
    const loadInitialData = async () => {
      // Use allSettled to allow each function to complete independently
      // Even if one fails, others will still execute
      // CRITICAL ONLY - removed preloadChats and checkPaymentStatus from initial load for faster startup
      const results = await Promise.allSettled([
        fetchLeaderboard(),
        fetchUnreadCount(),
        checkNewCommunityPosts(),
        fetchOnlineCount()
      ]);
      
      // Log any failures for debugging
      results.forEach((result, index) => {
        const names = ['fetchLeaderboard', 'fetchUnreadCount', 'checkNewCommunityPosts', 'fetchOnlineCount'];
        if (result.status === 'rejected') {
          console.error(`❌ ${names[index]} failed:`, result.reason);
        }
      });
      
      console.log('✅ Initial critical data loading complete');
      
      // Load non-critical data after a delay to not block initial render
      setTimeout(() => {
        console.log('🔄 Loading non-critical data...');
        preloadChats().catch(err => console.error('Preload chats failed:', err));
        checkPaymentStatus().catch(err => console.error('Check payment failed:', err));
      }, 2000); // 2 second delay for better perceived performance
    };
    
    loadInitialData();
    
    // Defer notification setup to not block initial render
    const setupNotifications = async () => {
      // Delay registration to allow UI to load first
      await new Promise(resolve => setTimeout(resolve, 500));
      await registerServiceWorker();
      
      // Check if running as PWA (from home screen)
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                          window.navigator.standalone || 
                          document.referrer.includes('android-app://');
      
      // Check if we've already asked for permission this session
      const hasAskedThisSession = sessionStorage.getItem('notification-prompt-shown');
      
      console.log('Is Standalone PWA:', isStandalone);
      console.log('Notification permission:', checkNotificationPermission());
      console.log('Has asked this session:', hasAskedThisSession);
      
      // Auto-prompt if: notifications enabled, permission not set, haven't asked yet
      // Show for ALL users (not just PWA) so badges work when app is closed
      if (user.notifications_enabled !== false && 
          checkNotificationPermission() === 'default' && 
          !hasAskedThisSession) {
        console.log('🔔 Showing notification prompt (needed for badges when app is closed)...');
        // Small delay so user sees the dashboard first
        setTimeout(() => {
          setShowNotificationPrompt(true);
          sessionStorage.setItem('notification-prompt-shown', 'true');
        }, 2000); // Increased to 2 seconds so user sees dashboard
      } else if (checkNotificationPermission() === 'granted') {
        console.log('✅ Push notifications already enabled - badges will update when app is closed');
      } else if (checkNotificationPermission() === 'denied') {
        console.log('❌ Notifications blocked - badges only work when app is open');
      }
    };
    setupNotifications();
    
    // Set up WebSocket for global notifications
    const wsUrl = `${API.replace('https://', 'wss://').replace('http://', 'ws://').replace('/api', '')}/ws/${user.id}`;
    const websocket = new WebSocket(wsUrl);
    
    websocket.onopen = () => {
      console.log('✅ LIVE NOTIFICATIONS ACTIVE - Message badges will update instantly');
    };
    
    websocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('📨 Dashboard WebSocket received message:', data);
      console.log('Full data:', JSON.stringify(data, null, 2));
      
      // Handle incoming message for notifications
      if (data.chat_id && data.sender_id && data.sender_id !== user.id) {
        console.log('✅ Message from another user detected');
        console.log('Current user ID:', user.id);
        console.log('Sender ID:', data.sender_id);
        console.log('Notifications enabled state:', notificationsEnabled);
        console.log('User notifications_enabled:', user.notifications_enabled);
        console.log('Notification permission:', checkNotificationPermission());
        
        // ALWAYS try to show notification if permission is granted
        const hasPermission = checkNotificationPermission() === 'granted';
        console.log('Has notification permission:', hasPermission);
        
        if (hasPermission) {
          console.log('🔔 FORCING notification call...');
          console.log('Notification data:', {
            title: 'Beypointer - New Message',
            sender: data.sender_username || data.username || 'New Message',
            message: data.content || data.message || '📷 Media',
            chatId: data.chat_id
          });
          try {
            // Show native PWA notification (works in background)
            showNotification('Beypointer - New Message', {
              sender: data.sender_username || data.username || 'New Message',
              message: data.content || data.message || '📷 Media',
              chatId: data.chat_id
            }).then(() => {
              console.log('✅ showNotification completed successfully');
            }).catch((error) => {
              console.error('❌ showNotification promise rejected:', error);
            });
            console.log('✓ showNotification called (promise pending)');
          } catch (error) {
            console.error('❌ Error calling showNotification:', error);
          }
          
          // Also show in-app notification when app is open
          setNotification({
            sender: data.sender_username || data.username || 'New Message',
            message: data.content || data.message || '📷 Media',
            chatId: data.chat_id
          });
          
          // Auto-hide in-app notification after 1 second
          setTimeout(() => {
            setNotification(null);
          }, 1000);
        } else {
          console.log('❌ No notification permission, cannot show notification');
          console.log('Go to Settings and click Test button to enable');
        }
        
        // Immediately increment unread count for instant feedback
        setUnreadCount(prev => {
          const newCount = prev + 1;
          console.log(`🔔 NEW MESSAGE! Badge updating: ${prev} → ${newCount}`);
          console.log('✅ MESSAGE BADGE SHOULD NOW BE VISIBLE ON DASHBOARD');
          // Update app badge immediately with new count
          if ('setAppBadge' in navigator) {
            navigator.setAppBadge(newCount).then(() => {
              console.log(`📱 PWA app badge updated: ${newCount} unread messages`);
            }).catch(err => {
              console.log('Badge API not supported:', err);
            });
          }
          return newCount;
        });
        
        // Fetch accurate count from server in background
        console.log('📊 Fetching accurate unread count from server...');
        fetchUnreadCount();
      } else {
        console.log('⚠️ Message check failed:');
        console.log('Has chat_id:', !!data.chat_id);
        console.log('Has sender_id:', !!data.sender_id);
        console.log('Is from other user:', data.sender_id !== user.id);
      }
    };
    
    websocket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
    
    websocket.onclose = () => {
      console.log('WebSocket disconnected');
    };
    
    wsRef.current = websocket;

    // Set up WebSocket for community post notifications
    const communityWsUrl = `${API.replace('https://', 'wss://').replace('http://', 'ws://').replace('/api', '')}/ws/community`;
    const communityWebSocket = new WebSocket(communityWsUrl);
    
    communityWebSocket.onopen = () => {
      console.log('✅ LIVE COMMUNITY UPDATES ACTIVE - Community badge will update instantly');
    };
    
    communityWebSocket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('📨 Community WebSocket received:', data);
      
      // Check if it's a new post notification (not from current user)
      if (data.type === 'new_post' && data.user_id !== user.id) {
        console.log('🎉 NEW COMMUNITY POST! Badge updating...');
        console.log('✅ COMMUNITY BADGE SHOULD NOW BE VISIBLE ON DASHBOARD');
        // Show the notification dot
        setHasNewCommunityPosts(true);
        
        // Update PWA app badge immediately
        if ('setAppBadge' in navigator) {
          navigator.setAppBadge(1).then(() => {
            console.log('📱 PWA app badge updated for community post');
          }).catch(err => {
            console.log('Badge API not supported:', err);
          });
        }
      }
    };
    
    communityWebSocket.onerror = (error) => {
      console.error('Community WebSocket error:', error);
    };
    
    communityWebSocket.onclose = () => {
      console.log('Community WebSocket disconnected');
    };
    
    communityWsRef.current = communityWebSocket;
    
    // Make showNotification globally accessible for debugging
    window.testNotificationFromDashboard = () => {
      console.log('Testing notification from Dashboard...');
      showNotification('Test from Dashboard', {
        sender: 'Dashboard Test',
        message: 'This is a test notification',
        chatId: 'test-dashboard'
      });
    };
    
    // Auto-refresh removed - no need to refresh once app is initially loaded
    // User can manually refresh if needed
    
    // Set up periodic unread message check and community posts check (every 2 minutes)
    const unreadCheckInterval = setInterval(() => {
      fetchUnreadCount();
      checkNewCommunityPosts(); // Re-added for notification badge
      // Note: preloadChats removed from interval to reduce load
      // It runs once on mount, which is sufficient
    }, 120000); // 120 seconds (2 minutes) - optimized frequency
    
    // Near real-time updates for online users and leaderboard (every 5 seconds)
    const liveUpdateInterval = setInterval(() => {
      fetchLeaderboard(); // This also syncs the online count
      sendHeartbeat(); // Update online activity status
    }, 5000); // 5 seconds for near real-time
    
    // Listen for PWA install prompt (Chrome/Edge/Android)
    const handleBeforeInstallPrompt = (e) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      // Stash the event so it can be triggered later
      setDeferredPrompt(e);
      console.log('✓ PWA install prompt captured and ready');
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Check if app is already installed
    window.addEventListener('appinstalled', () => {
      console.log('✓ PWA was installed');
      toast.success('App installed successfully!');
      setDeferredPrompt(null);
    });
    
    return () => {
      clearInterval(unreadCheckInterval);
      clearInterval(liveUpdateInterval);
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      // Close WebSocket connections
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (communityWsRef.current) {
        communityWsRef.current.close();
      }
      // Clear account status intervals
      clearInterval(statusInterval);
      clearInterval(fastCheckInterval);
    };
  }, [user]);

  // Real-time update for online users modal when open
  useEffect(() => {
    if (!showOnlineUsersModal) return;
    
    // Refresh immediately when modal opens
    fetchOnlineUsersList();
    
    // Then refresh every 5 seconds while modal is open
    const onlineModalInterval = setInterval(() => {
      fetchOnlineUsersList();
    }, 5000);
    
    return () => clearInterval(onlineModalInterval);
  }, [showOnlineUsersModal]);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      searchUsers();
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  // Close share menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showShareMenu && !e.target.closest('[data-testid="share-button"]') && !e.target.closest('.absolute.top-12')) {
        setShowShareMenu(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showShareMenu]);

  // Swipe detection for Send and Receive modals
  useEffect(() => {
    if (!showSendModal && !showReceiveModal) return;

    const handleTouchStart = (e) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchEnd = (e) => {
      const touchEndX = e.changedTouches[0].clientX;
      const touchEndY = e.changedTouches[0].clientY;
      
      const deltaX = touchEndX - touchStartX.current;
      const deltaY = touchEndY - touchStartY.current;
      
      // Check if horizontal swipe is dominant
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        const minSwipeDistance = 48; // ~0.5 inch at 96 DPI
        const now = Date.now();
        const cooldown = 1000; // 1 second
        
        if (Math.abs(deltaX) >= minSwipeDistance && now - lastSwipeTime.current >= cooldown) {
          lastSwipeTime.current = now;
          
          // Close the open modal
          if (showSendModal) {
            setShowSendModal(false);
          } else if (showReceiveModal) {
            setShowReceiveModal(false);
          }
        }
      }
    };

    document.addEventListener('touchstart', handleTouchStart);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [showSendModal, showReceiveModal]);

  const fetchLeaderboard = async (retryCount = 0) => {
    if (fetchingLeaderboard && retryCount === 0) {
      console.log('⏳ Leaderboard fetch already in progress, skipping');
      return; // Prevent concurrent requests
    }
    
    try {
      setFetchingLeaderboard(true);
      console.log('📊 Fetching leaderboard, friends_only:', friendsOnlyRef.current, retryCount > 0 ? `(retry #${retryCount})` : '');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/leaderboard`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { friends_only: friendsOnlyRef.current }
      });
      console.log('✅ Leaderboard fetched:', response.data.length, 'entries');
      setLeaderboard(response.data);
      // Sync online count with leaderboard count (they should match)
      if (!friendsOnlyRef.current) {
        setOnlineCount(response.data.length);
      }
      // Cache to localStorage so it stays mounted while logged in
      localStorage.setItem('leaderboardCache', JSON.stringify(response.data));
      setFetchingLeaderboard(false);
    } catch (error) {
      console.error('❌ Error fetching leaderboard:', error);
      setFetchingLeaderboard(false);
      // Only retry if leaderboard is still empty
      if (leaderboard.length === 0) {
        const delay = 500; // Retry every 500ms
        console.log(`🔄 Retrying leaderboard in ${delay}ms...`);
        setTimeout(() => fetchLeaderboard(retryCount + 1), delay);
      }
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/chat/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const count = response.data.total_unread || 0;
      console.log('💬 Unread message count:', count); // Debug log
      setUnreadCount(count);
      
      // Update app badge (PWA icon notification)
      if ('setAppBadge' in navigator) {
        if (count > 0) {
          navigator.setAppBadge(count).catch(err => {
            console.log('Badge API not supported:', err);
          });
        } else {
          // Clear badge when no unread messages
          navigator.clearAppBadge().catch(err => {
            console.log('Badge API not supported:', err);
          });
        }
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const checkNewCommunityPosts = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/community/has-new-posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHasNewCommunityPosts(response.data.has_new_posts || false);
    } catch (error) {
      console.error('Error checking new community posts:', error);
    }
  };

  const sendHeartbeat = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/users/heartbeat`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      // Silent fail - heartbeat is not critical
      console.debug('Heartbeat failed:', error);
    }
  };

  const fetchOnlineCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/online`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Backend now always includes current user
      setOnlineCount(response.data.online_count || 1);
    } catch (error) {
      console.error('Error fetching online count:', error);
      // If error, show at least 1 (current user is online)
      setOnlineCount(1);
    }
  };

  const fetchOnlineUsersList = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/online/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Backend now always includes current user and sorts by battle points
      setOnlineUsers(response.data.online_users || []);
    } catch (error) {
      console.error('Error fetching online users list:', error);
      toast.error('Failed to load online users');
    }
  };

  const handleOnlineCountClick = async () => {
    setShowOnlineUsersModal(true);
    await fetchOnlineUsersList();
  };

  const handleManualRefresh = async () => {
    console.log('Manual refresh triggered');
    await onUpdateUser();
    await fetchLeaderboard();
    toast.success('Balance refreshed!');
  };

  const preloadChats = async () => {
    try {
      console.log('🔄 Preloading chats in background...');
      const token = localStorage.getItem('token');
      
      if (!token) {
        console.error('❌ No token found for preload');
        return;
      }
      
      console.log('📡 Fetching chat list...');
      const response = await axios.get(`${API}/chat/list`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('📊 Chat list response:', response.data);
      
      // Cache chats for instant loading when Messages opens
      localStorage.setItem(`chats_${user.id}`, JSON.stringify(response.data));
      console.log('✅ Chats preloaded and cached for user:', user.id);
      console.log('💾 Cache key:', `chats_${user.id}`);
      
      // Preload messages for the most recent chats (up to 3 chats)
      const allChats = [...(response.data.direct_chats || []), ...(response.data.group_chats || [])];
      console.log('📝 Total chats found:', allChats.length);
      
      const recentChats = allChats
        .sort((a, b) => new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0))
        .slice(0, 1); // Reduced from 3 to 1 for faster loading
      
      console.log('🎯 Preloading', recentChats.length, 'most recent chat');
      
      // Preload messages for recent chats in PARALLEL for faster loading
      const messagePromises = recentChats.map(async (chat) => {
        try {
          console.log(`📥 Preloading messages for chat: ${chat.id}`);
          const messagesResponse = await axios.get(`${API}/chat/${chat.id}/messages`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          localStorage.setItem(`messages_${chat.id}`, JSON.stringify(messagesResponse.data.messages));
          console.log(`✅ Preloaded ${messagesResponse.data.messages.length} messages for chat: ${chat.id}`);
        } catch (err) {
          console.error(`❌ Error preloading messages for chat ${chat.id}:`, err);
        }
      });
      
      // Wait for all message preloads to complete in parallel
      await Promise.all(messagePromises);
      
      console.log('🎉 Preload complete!');
    } catch (error) {
      console.error('❌ Error preloading chats:', error);
      console.error('Error details:', error.response?.data || error.message);
    }
  };

  const checkPaymentStatus = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const paymentStatus = urlParams.get('payment');

    console.log('Checking payment status:', { sessionId, paymentStatus, fullURL: window.location.href });

    if (sessionId && paymentStatus === 'success') {
      console.log('Payment success detected, attempting automatic completion for session:', sessionId);
      setCheckingPayment(true);
      toast.info('Processing your payment...');
      
      // Try automatic completion first (works even if Stripe status is delayed)
      try {
        console.log('Attempting automatic manual-complete for session:', sessionId);
        const response = await axios.post(`${API}/purchase/manual-complete/${sessionId}`);
        console.log('Manual complete response:', response.data);
        
        if (response.data.success) {
          if (response.data.already_completed) {
            toast.info('Payment already processed. Refreshing...');
          } else {
            toast.success('🎉 Payment successful! 100 battle points added!');
          }
          setCheckingPayment(false);
          
          // Refresh to show new balance
          setTimeout(() => {
            window.location.reload();
          }, 1500);
          return;
        }
      } catch (error) {
        console.error('Auto-complete failed:', error);
        console.error('Error response:', error.response?.data);
        console.error('Session ID that failed:', sessionId);
        
        if (error.response?.status === 404) {
          // Error hidden - silent fail
          setCheckingPayment(false);
          return;
        }
        
        // If auto-complete fails, fall back to polling
        console.log('Falling back to verification polling');
      }
      
      // Fallback to polling if auto-complete didn't work
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === 'cancelled') {
      // Error hidden - silent fail
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5; // Reduced from 10 to 5 (5 seconds total)
    const pollInterval = 1000;

    console.log(`Polling payment status attempt ${attempts + 1}/${maxAttempts} for session:`, sessionId);

    if (attempts >= maxAttempts) {
      console.log('Polling failed, attempting automatic completion...');
      // Try manual complete automatically after polling fails
      try {
        const response = await axios.post(`${API}/purchase/manual-complete/${sessionId}`);
        if (response.data.success) {
          setCheckingPayment(false);
          toast.success('🎉 Payment completed! 100 battle points added!');
          setTimeout(() => {
            window.location.reload();
          }, 1500);
          return;
        }
      } catch (error) {
        console.error('Auto manual-complete also failed:', error);
      }
      
      // If everything fails, show manual button
      setCheckingPayment(false);
      setFailedSessionId(sessionId);
      setShowManualComplete(true);
      // Error hidden - silent fail
      return;
    }

    try {
      console.log('Calling checkout-status API for session:', sessionId);
      const response = await axios.get(`${API}/purchase/checkout-status/${sessionId}`);
      console.log('Checkout status response:', response.data);
      
      if (response.data.payment_status === 'paid') {
        setCheckingPayment(false);
        toast.success('🎉 Payment successful! 100 battle points added!');
        await onUpdateUser();
        await fetchLeaderboard();
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      } else if (response.data.status === 'expired') {
        setCheckingPayment(false);
        // Error hidden - silent fail
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      } else if (response.data.status === 'complete' && response.data.payment_status === 'paid') {
        setCheckingPayment(false);
        toast.success('🎉 Payment successful! 100 battle points added!');
        await onUpdateUser();
        await fetchLeaderboard();
        window.history.replaceState({}, document.title, window.location.pathname);
        return;
      }

      console.log('Payment still pending, retrying in 1 second...');
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        // Try auto manual-complete on final failure
        try {
          const response = await axios.post(`${API}/purchase/manual-complete/${sessionId}`);
          if (response.data.success) {
            setCheckingPayment(false);
            toast.success('🎉 Payment completed! 100 battle points added!');
            setTimeout(() => {
              window.location.reload();
            }, 1500);
            return;
          }
        } catch (completeError) {
          console.error('Auto complete failed:', completeError);
        }
        
        setCheckingPayment(false);
        setFailedSessionId(sessionId);
        setShowManualComplete(true);
        // Error hidden - silent fail
      }
    }
  };

  const handleManualComplete = async () => {
    if (!failedSessionId) return;
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/purchase/manual-complete/${failedSessionId}`);
      toast.success(response.data.message);
      setShowManualComplete(false);
      setFailedSessionId(null);
      
      // Force refresh to get updated points
      window.location.reload();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to complete payment manually';
      if (errorMsg === 'Payment already completed') {
        toast.info('This payment was already completed. Refreshing...');
        window.location.reload();
      } else {
        // Error hidden - silent fail
      }
      setLoading(false);
    }
  };

  const searchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users/search?q=${searchQuery}`);
      setSearchResults(response.data);
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  const handlePurchase = async () => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      const response = await axios.post(`${API}/purchase/create-checkout`, {
        package_id: "100bp",
        origin_url: originUrl
      });
      
      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      } else {
        // Error hidden - silent fail
      }
    } catch (error) {
      // Error hidden - silent fail
    } finally {
      setLoading(false);
    }
  };

  const handleSendPoints = async () => {
    if (!sendUsername || !sendPoints || parseInt(sendPoints) <= 0) {
      // Error hidden - silent fail
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/points/send`, {
        to_username: sendUsername,
        points: parseInt(sendPoints),
      });
      toast.success(response.data.message);
      setSendUsername('');
      setSelectedRecipient(null);
      setSendPoints('');
      setShowSendModal(false);
      await onUpdateUser();
      await fetchLeaderboard();
    } catch (error) {
      // Error hidden - silent fail
    } finally {
      setLoading(false);
    }
  };

  const handleQRScan = (scannedData) => {
    try {
      // Parse the scanned data (expected format: username)
      const username = scannedData.trim();
      if (username) {
        setSendUsername(username);
        setShowQRScanner(false);
        setShowSendModal(true);
        toast.success(`Scanned: ${username}`);
      } else {
        // Error hidden - silent fail
      }
    } catch (error) {
      // Error hidden - silent fail
    }
  };

  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      return; // Silent fail - error hidden
    }

    // Validate file size (5MB max before compression)
    if (file.size > 5 * 1024 * 1024) {
      return; // Silent fail - error hidden
    }

    setUploadingPicture(true);

    try {
      // Compress image before upload (max 100KB for profile pics)
      const compressedFile = await compressProfilePicture(file);
      
      // Convert compressed image to Base64
      const base64Image = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(compressedFile);
      });

      // Upload to backend
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/profile/upload-picture`,
        { profile_picture: base64Image },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Profile picture updated!');
      await onUpdateUser(); // Refresh user data
      setShowProfileModal(false);
    } catch (error) {
      // Error hidden - silent fail
    } finally {
      setUploadingPicture(false);
    }
  };

  // Delete Account - Hold button handlers
  const handleDeleteHoldStart = () => {
    deleteHoldStartRef.current = Date.now();
    setDeleteHoldProgress(0);
    
    // Update progress every 50ms
    deleteHoldTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - deleteHoldStartRef.current;
      const progress = Math.min((elapsed / 5000) * 100, 100);
      setDeleteHoldProgress(progress);
      
      // After 5 seconds, show confirmation modal
      if (progress >= 100) {
        clearInterval(deleteHoldTimerRef.current);
        setShowDeleteAccountModal(true);
        setDeleteHoldProgress(0);
      }
    }, 50);
  };

  const handleDeleteHoldEnd = () => {
    if (deleteHoldTimerRef.current) {
      clearInterval(deleteHoldTimerRef.current);
      deleteHoldTimerRef.current = null;
    }
    setDeleteHoldProgress(0);
  };

  const handleConfirmDeleteAccount = async () => {
    if (!deletePassword.trim()) {
      toast.error('Please enter your password');
      return;
    }

    setDeletingAccount(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/account/delete`,
        { password: deletePassword },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 60000 // 60 second timeout for account deletion
        }
      );

      if (response.data.success) {
        toast.success('Account deleted successfully');
        localStorage.removeItem('token');
        setShowDeleteAccountModal(false);
        setShowProfileModal(false);
        onLogout();
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Incorrect password');
      } else {
        toast.error('Failed to delete account');
      }
    } finally {
      setDeletingAccount(false);
      setDeletePassword('');
    }
  };

  const handleDeviceBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB before compression)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingDeviceBackground(true);

    try {
      // Compress image before upload (max 200KB)
      const compressedFile = await compressBannerImage(file);
      
      // Convert compressed image to Base64
      const base64Image = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(compressedFile);
      });

      // Upload to backend
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/profile/upload-device-background`,
        { profile_picture: base64Image },  // Reusing the same field name
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Device background updated!');
      await onUpdateUser(); // Refresh user data
      setShowDeviceBackgroundModal(false);
    } catch (error) {
      console.error('Error uploading device background:', error);
      toast.error('Failed to update device background');
    } finally {
      setUploadingDeviceBackground(false);
    }
  };

  const handleRemoveDeviceBackground = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/profile/device-background`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Device background removed!');
      await onUpdateUser();
      setShowDeviceBackgroundModal(false);
    } catch (error) {
      console.error('Error removing device background:', error);
    }
  };

  const handleOuterDeviceBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingOuterDeviceBackground(true);

    try {
      // Convert to Base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;

        // Upload to backend
        const token = localStorage.getItem('token');
        await axios.post(
          `${API}/profile/upload-outer-device-background`,
          { profile_picture: base64Image },  // Reusing the same field name
          { headers: { Authorization: `Bearer ${token}` } }
        );

        toast.success('Outer device background updated!');
        await onUpdateUser(); // Refresh user data
        setShowOuterDeviceBackgroundModal(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading outer device background:', error);
      toast.error('Failed to update outer device background');
    } finally {
      setUploadingOuterDeviceBackground(false);
    }
  };

  const handleRemoveOuterDeviceBackground = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/profile/outer-device-background`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Outer device background removed!');
      await onUpdateUser();
      setShowOuterDeviceBackgroundModal(false);
    } catch (error) {
      console.error('Error removing outer device background:', error);
    }
  };

  const handleLeaderboardBackgroundUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingLeaderboardBackground(true);

    try {
      // Convert to Base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;

        // Upload to backend
        const token = localStorage.getItem('token');
        await axios.post(
          `${API}/profile/upload-leaderboard-background`,
          { profile_picture: base64Image },  // Reusing the same field name
          { headers: { Authorization: `Bearer ${token}` } }
        );

        toast.success('Leaderboard background updated!');
        await onUpdateUser(); // Refresh user data
        setShowLeaderboardBackgroundModal(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading leaderboard background:', error);
      toast.error('Failed to update leaderboard background');
    } finally {
      setUploadingLeaderboardBackground(false);
    }
  };

  const handleRemoveLeaderboardBackground = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/profile/leaderboard-background`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Leaderboard background removed!');
      await onUpdateUser();
      setShowLeaderboardBackgroundModal(false);
    } catch (error) {
      console.error('Error removing leaderboard background:', error);
    }
  };

  const handleBannerUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB before compression)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingBanner(true);

    try {
      // Compress image before upload (max 200KB)
      const compressedFile = await compressBannerImage(file);
      
      // Convert compressed image to Base64
      const base64Image = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(compressedFile);
      });

      // Upload to backend
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/profile/upload-banner`,
        { profile_picture: base64Image },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Banner updated!');
      await onUpdateUser(); // Refresh user data
      setShowBannerModal(false);
    } catch (error) {
      console.error('Error uploading banner:', error);
      toast.error('Failed to update banner');
    } finally {
      setUploadingBanner(false);
    }
  };

  const handleRemoveBanner = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/profile/banner`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Banner removed!');
      await onUpdateUser();
      setShowBannerModal(false);
    } catch (error) {
      console.error('Error removing banner:', error);
    }
  };

  const handleCircleColorUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingCircleColor(true);

    try {
      // Convert to Base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Image = reader.result;

        // Upload to backend
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API}/profile/upload-circle-color`,
          { image: base64Image },
          { headers: { Authorization: `Bearer ${token}` } }
        );

        toast.success(`Circle color updated to ${response.data.circle_color}!`);
        await onUpdateUser(); // Refresh user data
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading circle color:', error);
      toast.error('Failed to update circle color');
    } finally {
      setUploadingCircleColor(false);
    }
  };

  const handleResetCircleColor = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/profile/reset-circle-color`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Circle color reset to default!');
      await onUpdateUser();
    } catch (error) {
      console.error('Error resetting circle color:', error);
      toast.error('Failed to reset circle color');
    }
  };

  const handleShareApp = async () => {
    setShowShareMenu(false);
    const shareData = {
      title: 'Beypointer: PvP Battle Arena',
      text: 'Join me on Beypointer: PvP Battle Arena! Exchange battle points with friends.',
      url: 'https://beypointer.com/'
    };

    // Check if Web Share API is available (mobile devices)
    if (navigator.share) {
      try {
        await navigator.share(shareData);
        toast.success('App shared successfully!');
      } catch (error) {
        // User cancelled share or error occurred - silent fail
      }
    } else {
      // Fallback: Copy link to clipboard
      try {
        await navigator.clipboard.writeText('Join me on Beypointer: PvP Battle Arena! Exchange battle points with friends. https://beypointer.com/');
        toast.success('App link copied to clipboard!');
      } catch (error) {
        // Error hidden - silent fail
      }
    }
  };

  const handleToggleMessages = async (enabled) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/profile/toggle-messages?enabled=${enabled}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessagesEnabled(enabled);
      await onUpdateUser(); // Refresh user data
      toast.success(enabled ? 'Messages enabled' : 'Messages disabled');
    } catch (error) {
      // Error hidden - silent fail
    }
  };

  const handleToggleNotifications = async (enabled) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/profile/toggle-notifications?enabled=${enabled}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotificationsEnabled(enabled);
      await onUpdateUser(); // Refresh user data
      toast.success(enabled ? 'Notifications enabled' : 'Notifications disabled');
    } catch (error) {
      console.error('Error toggling notifications:', error);
      toast.error('Failed to update notification settings');
    }
  };

  const handleToggleFriendsOnlyMessages = async (enabled) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/profile/toggle-friends-only-messages?enabled=${enabled}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFriendsOnlyMessages(enabled);
      await onUpdateUser(); // Refresh user data
      toast.success(enabled ? 'Now accepting messages only from mutual friends' : 'Now accepting messages from all users');
    } catch (error) {
      console.error('Error toggling friends only messages:', error);
      toast.error('Failed to update message settings');
    }
  };

  const handleOpenMessages = () => {
    // Clear unread count when opening messages
    setUnreadCount(0);
    
    // Clear app badge
    if ('clearAppBadge' in navigator) {
      navigator.clearAppBadge().catch(err => {
        console.log('Badge API not supported:', err);
      });
    }
    
    onOpenMessages();
  };

  const handleMessageUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/chat/direct/create?other_user_id=${userId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Open messages view with this chat
      onOpenMessages();
      toast.success('Opening chat...');
    } catch (error) {
      if (error.response?.status === 403) {
        toast.info('This user has disabled messages');
      }
      // Other errors hidden - silent fail
    }
  };

  const handleAddToHomeScreen = async () => {
    setShowShareMenu(false);
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      toast.info('App is already installed!');
      return;
    }
    
    // Check for iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const isInStandaloneMode = ('standalone' in window.navigator) && (window.navigator.standalone);
    
    if (isIOS && !isInStandaloneMode) {
      // iOS requires manual installation
      toast.info(
        <div className="flex flex-col gap-2">
          <div className="font-semibold">To install on iOS:</div>
          <div className="text-sm">
            1. Tap the <strong>Share</strong> button (bottom toolbar)<br/>
            2. Scroll and tap <strong>"Add to Home Screen"</strong><br/>
            3. Tap <strong>"Add"</strong>
          </div>
        </div>,
        { duration: 8000 }
      );
      return;
    }
    
    // Try PWA install prompt (Chrome/Edge/Android)
    if (deferredPrompt) {
      try {
        // Show the install prompt
        await deferredPrompt.prompt();
        
        // Wait for the user's response
        const { outcome } = await deferredPrompt.userChoice;
        
        if (outcome === 'accepted') {
          toast.success('App installed successfully!');
          setDeferredPrompt(null);
        } else {
          toast.info('Installation cancelled');
        }
      } catch (error) {
        console.error('Install prompt error:', error);
        toast.info('Unable to install. Try using browser menu: "Install App" or "Add to Home Screen"');
      }
    } else {
      // No install prompt available - guide user to browser menu
      const isChrome = /Chrome/.test(navigator.userAgent);
      const isEdge = /Edg/.test(navigator.userAgent);
      
      if (isChrome || isEdge) {
        toast.info(
          'To install: Tap the menu (⋮) → "Add to Home Screen" or "Install app"',
          { duration: 6000 }
        );
      } else {
        toast.info('Use your browser menu to add this app to your home screen', { duration: 5000 });
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-4 md:p-8 relative overflow-hidden" style={{ paddingTop: 'calc(env(safe-area-inset-top) + 2.5rem)' }}>
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl top-20 left-20 animate-pulse-slow"></div>
        <div className="absolute w-96 h-96 bg-orange-500/5 rounded-full blur-3xl bottom-20 right-20 animate-pulse-slow" style={{ animationDelay: '1s' }}></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-2 relative z-[60]">
          <div className="flex items-start gap-2">
            {/* Profile Picture and Verification Status */}
            <div className="flex flex-col items-center gap-1">
              <button
                onClick={() => setShowProfileModal(true)}
                className="group relative flex-shrink-0"
                title="Change profile picture"
              >
                {user.profile_picture ? (
                  <img
                    src={user.profile_picture}
                    alt={user.username}
                    className="rounded-full object-cover border-2 transition-colors"
                    style={{ 
                      width: '64px',
                      height: '64px',
                      minWidth: '64px',
                      minHeight: '64px',
                      borderColor: user.circle_color || '#475569'
                    }}
                  />
                ) : (
                  <div 
                    className="rounded-full bg-slate-700 border-2 flex items-center justify-center text-slate-400 font-semibold text-xl transition-colors"
                    style={{ 
                      width: '64px',
                      height: '64px',
                      minWidth: '64px',
                      minHeight: '64px',
                      borderColor: user.circle_color || '#475569'
                    }}
                  >
                    {user.username.charAt(0).toUpperCase()}
                  </div>
                )}
              </button>
              
              {/* Verified/Unverified Status Button */}
              {accountStatus?.is_permanent ? (
                <div
                  className="text-[7px] font-semibold px-1.5 py-px rounded-full flex items-center gap-0.5 bg-blue-500/20 text-blue-400 cursor-default"
                  title="Permanent Account"
                >
                  <svg className="w-2 h-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Verified
                </div>
              ) : (
                <button
                  onClick={() => setShowVerifyAccountModal(true)}
                  className="text-[7px] font-semibold px-1.5 py-px rounded-full flex items-center gap-0.5 transition-colors bg-orange-500/20 text-orange-400 hover:bg-orange-500/30"
                  title="Temporary Account - Click for info"
                >
                  <div className="w-1.5 h-1.5 bg-orange-400 rounded-full animate-pulse"></div>
                  Unverified
                </button>
              )}
            </div>
            
            <div className="flex flex-col gap-2">
              {/* Username Banner - Leaderboard Style */}
              <div 
                onClick={() => setShowBannerModal(true)}
                className="leaderboard-entry bg-slate-800/30 border border-slate-700/50 rounded-lg px-4 py-3 cursor-pointer hover:border-cyan-500/50 transition-colors relative"
                style={user.banner_image ? {
                  backgroundImage: `url(${user.banner_image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                } : {}}
                title="Click to customize banner"
              >
                {/* Overlay for better text visibility when banner is set */}
                {user.banner_image && (
                  <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                )}
                
                {/* Level Badge */}
                <LevelBadge level={user.level || 1} small={true} verified={accountStatus?.is_permanent} />
                
                {/* Trophy System - Top Right */}
                <div className="absolute top-1 right-2 flex items-center gap-1 z-10">
                  <div className="flex items-center gap-0.5">
                    <Trophy className="w-3 h-3 text-yellow-500" />
                    <span className="text-[10px] font-bold text-white drop-shadow">{user.gold_trophies || 0}</span>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <Trophy className="w-3 h-3 text-slate-300" />
                    <span className="text-[10px] font-bold text-white drop-shadow">{user.silver_trophies || 0}</span>
                  </div>
                  <div className="flex items-center gap-0.5">
                    <Trophy className="w-3 h-3 text-amber-600" />
                    <span className="text-[10px] font-bold text-white drop-shadow">{user.bronze_trophies || 0}</span>
                  </div>
                </div>
                
                <h2 className={`text-2xl font-semibold relative z-10 ${user.banner_image ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`} data-testid="user-username">
                  {user.username}
                </h2>
                
                {/* EXP Progress Bar */}
                <ExpProgressBar level={user.level || 1} exp={user.exp || 0} lower={true} />
              </div>
              
              {/* Compact Buttons under username with Messages Icon */}
              <div className="flex gap-1 items-center">
                <Button
                  onClick={onOpenTournament}
                  size="sm"
                  className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white text-[9px] pl-0 pr-1 py-[7px] h-auto"
                >
                  <Trophy className="w-2.5 h-2.5 mr-0.5 ml-0.5" />
                  Bracket
                </Button>
                <Button
                  onClick={() => {
                    setHasNewCommunityPosts(false);
                    // Clear PWA app badge when opening community
                    if ('clearAppBadge' in navigator) {
                      navigator.clearAppBadge().catch(err => {
                        console.log('Badge API not supported:', err);
                      });
                    }
                    onOpenCommunityPosts();
                  }}
                  size="sm"
                  className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white text-[9px] pl-0 pr-1 py-[7px] h-auto relative"
                >
                  <Users className="w-2.5 h-2.5 mr-0.5 ml-0.5" />
                  Community
                  {hasNewCommunityPosts && (
                    <span className="absolute top-0 right-0 w-1.5 h-1.5 bg-cyan-400 rounded-full animate-pulse"></span>
                  )}
                </Button>
                <Button
                  onClick={onOpenEvents}
                  size="sm"
                  className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white text-[9px] pl-0 pr-1 py-[7px] h-auto"
                >
                  <Calendar className="w-2.5 h-2.5 mr-0.5 ml-0.5" />
                  Events
                </Button>
                
                {/* Messages Icon on the right */}
                <button
                  onClick={handleOpenMessages}
                  className="text-cyan-400 hover:text-cyan-300 transition-colors p-1 hover:bg-cyan-500/10 rounded-lg relative flex items-center justify-center ml-1"
                  title="Messages"
                >
                  <MessageCircle className="w-7 h-7" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-4 h-4 flex items-center justify-center text-[10px]">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-1 items-end">
            <Button
              onClick={onLogout}
              data-testid="logout-button"
              variant="outline"
              size="sm"
              className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
            >
              Logout
            </Button>
            
            <div className="flex flex-col items-end gap-1">
              <div className="flex items-center gap-2 relative">
                <button
                  onClick={() => setShowSettingsModal(true)}
                  className="text-cyan-400 hover:text-cyan-300 transition-colors p-2 hover:bg-cyan-500/10 rounded-lg"
                  title="Settings"
                >
                  <Settings className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setShowShareMenu(!showShareMenu)}
                  data-testid="share-button"
                  className="text-cyan-400 hover:text-cyan-300 transition-colors p-2 hover:bg-cyan-500/10 rounded-lg"
                  title="Share & Install"
                >
                  <Share2 className="w-5 h-5" />
                </button>
              </div>
              
              {/* Store Button */}
              <button
                onClick={onOpenStore}
                className="transition-colors p-1.5 hover:bg-slate-700/50 rounded-lg flex items-center gap-1 relative z-[60]"
                title="Store"
                style={{
                  animation: 'spin-glow-purple 6s linear infinite'
                }}
              >
                <span className="text-xs text-purple-400 hover:text-purple-300">Store</span>
                <Gift className="w-5 h-5 text-red-500" />
              </button>
              
              <style>{`
                @keyframes spin-glow-purple {
                  0% {
                    box-shadow: 
                      2px 0 6px rgba(192, 132, 252, 0.9),
                      1px 0 3px rgba(192, 132, 252, 0.6);
                  }
                  25% {
                    box-shadow: 
                      0 2px 6px rgba(192, 132, 252, 0.9),
                      0 1px 3px rgba(192, 132, 252, 0.6);
                  }
                  50% {
                    box-shadow: 
                      -2px 0 6px rgba(192, 132, 252, 0.9),
                      -1px 0 3px rgba(192, 132, 252, 0.6);
                  }
                  75% {
                    box-shadow: 
                      0 -2px 6px rgba(192, 132, 252, 0.9),
                      0 -1px 3px rgba(192, 132, 252, 0.6);
                  }
                  100% {
                    box-shadow: 
                      2px 0 6px rgba(192, 132, 252, 0.9),
                      1px 0 3px rgba(192, 132, 252, 0.6);
                  }
                }
              `}</style>
              
              {/* Share Menu Dropdown */}
              {showShareMenu && (
                <div className="absolute top-12 right-0 bg-slate-800 border border-slate-700 rounded-lg shadow-lg overflow-hidden z-[70] min-w-[200px]">
                  <button
                    onClick={handleShareApp}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-700 transition-colors text-left text-white"
                  >
                    <Share2 className="w-4 h-4 text-cyan-400" />
                    <span>Share App</span>
                  </button>
                  <button
                    onClick={handleAddToHomeScreen}
                    className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-700 transition-colors text-left text-white border-t border-slate-700"
                  >
                    <Download className="w-4 h-4 text-cyan-400" />
                    <span>Add to Home Screen</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Beypointer Device - Spans 2 columns */}
          <div className="lg:col-span-2">
            <div 
              className="beypointer-container max-w-2xl mx-auto relative"
              style={user.outer_device_background ? {
                backgroundImage: `url(${user.outer_device_background})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              } : {}}
            >
              {/* Online Users Count - Top Left Corner */}
              <button
                onClick={handleOnlineCountClick}
                className="absolute top-1 left-2 z-10 flex items-center gap-1.5 text-white text-xs font-semibold px-2 py-1 hover:text-cyan-300 transition-colors cursor-pointer"
                title="View online users"
              >
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-green-400">Online:</span>
                <span>{onlineCount}</span>
              </button>
              
              {/* Map Button - Centered between Online and Battle */}
              <button
                onClick={onOpenMap}
                className="absolute top-1 left-1/2 -translate-x-[65%] z-10 text-white hover:text-cyan-300 px-3 py-1.5 text-xs font-semibold transition-colors flex items-center gap-1"
                title="Find nearby bladers"
              >
                <MapPin className="w-4 h-4" />
                Find Bladers
              </button>

              {/* Battle Button - Top Right */}
              <button
                onClick={onOpenBattle}
                className="absolute top-1 right-20 z-10 text-red-500 hover:text-red-400 px-3 py-1.5 text-xs font-semibold transition-colors"
                title="Battle in the stadium"
              >
                Battle Arena
              </button>

              {/* Customize Button for Outer Device */}
              <button
                onClick={() => setShowOuterDeviceBackgroundModal(true)}
                className="absolute top-1 right-2 text-cyan-400 hover:text-cyan-300 px-3 py-1.5 text-xs font-semibold transition-colors z-10"
                title="Customize device background"
              >
                Customize
              </button>

              {/* Device Header */}
              <div className="text-center mb-6">
                <h1 className="text-3xl md:text-4xl font-bold digital-font text-cyan-400 glow-cyan">
                  BEYPOINTER
                </h1>
              </div>

              {/* LCD Display */}
              <div 
                className="lcd-screen p-8 mb-8 relative"
                style={user.device_background ? {
                  backgroundImage: `url(${user.device_background})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                } : {}}
              >
                {/* Overlay for better text visibility when background is set */}
                {user.device_background && (
                  <div className="absolute inset-0 bg-black/30 rounded-lg"></div>
                )}
                
                {/* Customize button in top right corner */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeviceBackgroundModal(true);
                  }}
                  className="absolute top-1 right-1 z-20 text-cyan-500 hover:text-cyan-400 px-3 py-1.5 text-xs font-semibold transition-colors"
                >
                  Customize
                </button>

                <div className="text-center space-y-2 relative z-10">
                  <div className="flex items-center justify-center gap-2">
                    <div className={`text-sm font-medium ${user.device_background ? 'text-white drop-shadow-lg' : 'text-slate-700'}`}>
                      BATTLE POINTS
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleManualRefresh();
                      }}
                      className={`p-1 rounded hover:bg-slate-700/20 transition-colors ${user.device_background ? 'text-white' : 'text-slate-700'}`}
                      title="Refresh balance"
                    >
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  </div>
                  <div
                    className={`text-6xl md:text-7xl digital-font font-black tracking-wider ${user.device_background ? 'text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]' : 'text-slate-900'}`}
                    data-testid="battle-points-display"
                  >
                    {user.battle_points.toString().padStart(4, '0')}
                  </div>
                  <div className={`text-xs ${user.device_background ? 'text-white drop-shadow-lg' : 'text-slate-700'}`}>
                    ポイント
                  </div>
                  {checkingPayment && (
                    <div className={`mt-3 text-xs font-semibold animate-pulse ${user.device_background ? 'text-white drop-shadow-lg' : 'text-slate-700'}`}>
                      VERIFYING PAYMENT...
                    </div>
                  )}
                </div>
              </div>

              {/* Control Buttons */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <button
                  onClick={() => setShowSendModal(true)}
                  data-testid="send-points-button"
                  className="device-button rounded-xl p-6 flex flex-col items-center justify-center space-y-3 hover:bg-gradient-to-br hover:from-slate-700 hover:to-slate-800"
                >
                  <Send className="w-8 h-8 text-orange-400" />
                  <span className="text-white font-semibold text-lg">SEND</span>
                </button>

                <button
                  onClick={() => setShowReceiveModal(true)}
                  data-testid="receive-points-button"
                  className="device-button rounded-xl p-6 flex flex-col items-center justify-center space-y-3 hover:bg-gradient-to-br hover:from-slate-700 hover:to-slate-800"
                >
                  <ArrowDownToLine className="w-8 h-8 text-cyan-400" />
                  <span className="text-white font-semibold text-lg">RECEIVE</span>
                </button>
              </div>

              {/* Purchase Button */}
              <Button
                onClick={handlePurchase}
                data-testid="purchase-button"
                disabled={loading}
                className="w-full bg-cyan-600 hover:bg-cyan-700 text-white font-semibold py-6 text-lg shadow-lg hover:shadow-cyan-500/20 transition-all duration-300"
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                {loading ? 'Processing...' : 'PURCHASE 100BP   $1'}
              </Button>

              {/* Manual Complete Button - Shows when payment verification fails */}
              {showManualComplete && (
                <div className="mt-4">
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-3">
                    <p className="text-red-400 text-sm text-center mb-2">
                      ⚠️ Payment verification failed
                    </p>
                    <p className="text-red-300 text-xs text-center">
                      If your card was charged, click the button below to complete your purchase
                    </p>
                  </div>
                  <Button
                    onClick={handleManualComplete}
                    data-testid="manual-complete-button"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-semibold py-6 text-lg shadow-lg hover:shadow-green-500/20 transition-all duration-300"
                  >
                    {loading ? 'Completing...' : '✓ I Was Charged - Add My Points'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Leaderboard Section */}
          <div className="lg:col-span-1">
            <div 
              className="beypointer-container h-full relative"
              style={user.leaderboard_background ? {
                backgroundImage: `url(${user.leaderboard_background})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              } : {}}
            >
              {/* Customize Button for Leaderboard */}
              <button
                onClick={() => setShowLeaderboardBackgroundModal(true)}
                className="absolute top-1 right-2 text-cyan-400 hover:text-cyan-300 px-3 py-1.5 text-xs font-semibold transition-colors z-10"
                title="Customize leaderboard background"
              >
                Customize
              </button>

              <div className="flex items-center gap-2 mb-4">
                <Trophy className="w-5 h-5 text-yellow-400" />
                <h3 className="text-xl font-bold text-cyan-400 digital-font">LEADERBOARD</h3>
                <button
                  onClick={() => {
                    console.log('🔄 Toggle clicked, current friendsOnly:', friendsOnly);
                    setFriendsOnly(!friendsOnly);
                  }}
                  className={`px-2 py-0.5 text-[10px] font-semibold rounded transition-colors ${
                    friendsOnly 
                      ? 'bg-cyan-500 text-white hover:bg-cyan-600' 
                      : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                  }`}
                  title={friendsOnly ? 'Showing friends only' : 'Showing all users'}
                >
                  {friendsOnly ? 'Friends' : 'All'}
                </button>
              </div>

              {/* Search Bar */}
              <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input
                  type="text"
                  data-testid="search-input"
                  placeholder="Search bladers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500 focus:ring-cyan-500/20"
                />
              </div>

              {/* Search Results or Leaderboard */}
              <div className="space-y-2 max-h-[500px] overflow-y-auto scrollbar-thin" data-testid="leaderboard-container">
                {searchQuery.length >= 2 && searchResults.length > 0 ? (
                  searchResults.map((result, index) => (
                    <div
                      key={index}
                      data-testid={`search-result-${index}`}
                      className="leaderboard-entry bg-slate-800/30 border border-slate-700/50 rounded-lg p-3 flex justify-between items-center relative"
                      style={result.banner_image ? {
                        backgroundImage: `url(${result.banner_image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      } : {}}
                    >
                      {/* Overlay for better text visibility when banner is set */}
                      {result.banner_image && (
                        <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                      )}
                      
                      {/* Level Badge */}
                      <LevelBadge level={result.level || 1} small={true} verified={result.has_made_first_purchase} />
                      
                      <div className="flex items-center gap-3 relative z-10">
                        {result.profile_picture ? (
                          <img
                            src={result.profile_picture}
                            alt={result.username}
                            className="w-12 h-12 rounded-full object-cover border-2 pointer-events-none"
                            style={{ borderColor: result.circle_color || '#475569' }}
                          />
                        ) : (
                          <div 
                            className="w-12 h-12 rounded-full bg-slate-700 border-2 flex items-center justify-center text-slate-300 text-sm font-semibold pointer-events-none"
                            style={{ borderColor: result.circle_color || '#475569' }}
                          >
                            {result.username.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className={`font-medium ${result.banner_image ? 'text-white drop-shadow-lg' : 'text-slate-200'}`}>{result.username}</span>
                      </div>
                      <span className={`digital-font font-semibold relative z-10 ${result.banner_image ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                        {result.battle_points} BP
                      </span>
                      
                      {/* Trophy System - Top Right */}
                      <div className="absolute top-1 right-2 flex items-center gap-1 z-10">
                        <div className="flex items-center gap-0.5">
                          <Trophy className="w-3 h-3 text-yellow-500" />
                          <span className="text-[10px] font-bold text-white drop-shadow">{result.gold_trophies || 0}</span>
                        </div>
                        <div className="flex items-center gap-0.5">
                          <Trophy className="w-3 h-3 text-slate-300" />
                          <span className="text-[10px] font-bold text-white drop-shadow">{result.silver_trophies || 0}</span>
                        </div>
                        <div className="flex items-center gap-0.5">
                          <Trophy className="w-3 h-3 text-amber-600" />
                          <span className="text-[10px] font-bold text-white drop-shadow">{result.bronze_trophies || 0}</span>
                        </div>
                      </div>
                      
                      {/* EXP Progress Bar */}
                      <ExpProgressBar level={result.level || 1} exp={result.exp || 0} />
                    </div>
                  ))
                ) : searchQuery.length >= 2 ? (
                  <div className="text-center text-slate-500 py-8">No bladers found</div>
                ) : (
                  leaderboard.map((entry, index) => (
                    <div
                      key={index}
                      data-testid={`leaderboard-entry-${index}`}
                      className="leaderboard-entry bg-slate-800/30 border border-slate-700/50 rounded-lg p-3 flex justify-between items-center relative"
                      style={entry.banner_image ? {
                        backgroundImage: `url(${entry.banner_image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      } : {}}
                    >
                      {/* Overlay for better text visibility when banner is set */}
                      {entry.banner_image && (
                        <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                      )}
                      
                      {/* Level Badge */}
                      <LevelBadge level={entry.level || 1} small={true} verified={entry.has_made_first_purchase} />
                      
                      <div className="flex items-center gap-3 relative z-10">
                        {/* Rank Number */}
                        <div
                          className={`text-xl font-bold min-w-[32px] text-center ${
                            index === 0
                              ? 'text-yellow-500'
                              : index === 1
                              ? 'text-slate-300'
                              : index === 2
                              ? 'text-amber-600'
                              : 'text-slate-400'
                          }`}
                        >
                          {index + 1}
                        </div>
                        {/* Profile Picture */}
                        {entry.profile_picture ? (
                          <img
                            src={entry.profile_picture}
                            alt={entry.username}
                            className="w-12 h-12 rounded-full object-cover border-2 pointer-events-none"
                            style={{ borderColor: entry.circle_color || '#475569' }}
                          />
                        ) : (
                          <div 
                            className="w-12 h-12 rounded-full bg-slate-700 border-2 flex items-center justify-center text-slate-300 text-sm font-semibold pointer-events-none"
                            style={{ borderColor: entry.circle_color || '#475569' }}
                          >
                            {entry.username.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className={`font-medium ${entry.banner_image ? 'text-white drop-shadow-lg' : 'text-slate-200'}`}>{entry.username}</span>
                      </div>
                      <span className={`digital-font font-semibold relative z-10 ${entry.banner_image ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                        {entry.battle_points} BP
                      </span>
                      
                      {/* Trophy System - Top Right */}
                      <div className="absolute top-1 right-2 flex items-center gap-1 z-10">
                        {/* Gold Trophy */}
                        <div className="flex items-center gap-0.5">
                          <Trophy className="w-3 h-3 text-yellow-500" />
                          <span className="text-[10px] font-bold text-white drop-shadow">{entry.gold_trophies || 0}</span>
                        </div>
                        {/* Silver Trophy */}
                        <div className="flex items-center gap-0.5">
                          <Trophy className="w-3 h-3 text-slate-300" />
                          <span className="text-[10px] font-bold text-white drop-shadow">{entry.silver_trophies || 0}</span>
                        </div>
                        {/* Bronze Trophy */}
                        <div className="flex items-center gap-0.5">
                          <Trophy className="w-3 h-3 text-amber-600" />
                          <span className="text-[10px] font-bold text-white drop-shadow">{entry.bronze_trophies || 0}</span>
                        </div>
                      </div>
                      
                      {/* EXP Progress Bar */}
                      <ExpProgressBar level={entry.level || 1} exp={entry.exp || 0} />
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Send Points Modal */}
      <Dialog open={showSendModal} onOpenChange={setShowSendModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white top-[10%] translate-y-0 max-w-2xl" data-testid="send-modal">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 digital-font text-xl">SEND BATTLE POINTS</DialogTitle>
            <DialogDescription className="text-slate-400">
              Search and select a blader to send points
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="send-username" className="text-slate-300">
                Search for Recipient
              </Label>
              <div className="flex gap-2">
                <Input
                  id="send-username"
                  data-testid="send-username-input"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search by username..."
                  className="bg-slate-800 border-slate-700 text-white flex-1"
                />
                <Button
                  onClick={() => {
                    setShowSendModal(false);
                    setShowQRScanner(true);
                  }}
                  data-testid="scan-qr-button"
                  variant="outline"
                  className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/10"
                  title="Scan QR Code"
                >
                  <QrCode className="w-5 h-5" />
                </Button>
              </div>
              
              {/* Search Results */}
              {searchQuery.length >= 2 && searchResults.length > 0 && (
                <div className="max-h-64 overflow-y-auto space-y-2 mt-3">
                  {searchResults.map((result) => (
                    <button
                      key={result.username}
                      onClick={() => {
                        setSendUsername(result.username);
                        setSelectedRecipient(result);
                        setSearchQuery('');
                      }}
                      className="w-full leaderboard-entry bg-slate-800/30 border border-slate-700/50 rounded-lg p-4 hover:bg-slate-700 transition-colors flex justify-between items-center relative"
                      style={result.banner_image ? {
                        backgroundImage: `url(${result.banner_image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat'
                      } : {}}
                    >
                      {/* Overlay for better text visibility when banner is set */}
                      {result.banner_image && (
                        <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                      )}
                      
                      {/* Level Badge */}
                      <LevelBadge level={result.level || 1} small={true} verified={result.has_made_first_purchase} />
                      
                      <div className="flex items-center gap-3 relative z-10">
                        {/* Profile Picture */}
                        {result.profile_picture ? (
                          <img
                            src={result.profile_picture}
                            alt={result.username}
                            className="w-12 h-12 rounded-full object-cover border-2 flex-shrink-0"
                            style={{ borderColor: result.circle_color || '#475569' }}
                          />
                        ) : (
                          <div 
                            className="w-12 h-12 rounded-full bg-slate-700 border-2 flex items-center justify-center text-slate-400 font-semibold text-sm flex-shrink-0"
                            style={{ borderColor: result.circle_color || '#475569' }}
                          >
                            {result.username.charAt(0).toUpperCase()}
                          </div>
                        )}
                        <span className={`font-medium ${result.banner_image ? 'text-white drop-shadow-lg' : 'text-slate-200'}`}>
                          {result.username}
                        </span>
                      </div>
                      
                      <span className={`digital-font font-semibold relative z-10 ${result.banner_image ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                        {result.battle_points} BP
                      </span>
                      
                      {/* Trophy System - Top Right */}
                      <div className="absolute top-1 right-2 flex items-center gap-1 z-10">
                        {/* Gold Trophy */}
                        <div className="flex items-center gap-0.5">
                          <Trophy className="w-3 h-3 text-yellow-500" />
                          <span className="text-[10px] font-bold text-white drop-shadow">{result.gold_trophies || 0}</span>
                        </div>
                        {/* Silver Trophy */}
                        <div className="flex items-center gap-0.5">
                          <Trophy className="w-3 h-3 text-slate-300" />
                          <span className="text-[10px] font-bold text-white drop-shadow">{result.silver_trophies || 0}</span>
                        </div>
                        {/* Bronze Trophy */}
                        <div className="flex items-center gap-0.5">
                          <Trophy className="w-3 h-3 text-amber-600" />
                          <span className="text-[10px] font-bold text-white drop-shadow">{result.bronze_trophies || 0}</span>
                        </div>
                      </div>
                      
                      {/* EXP Progress Bar */}
                      <ExpProgressBar level={result.level || 1} exp={result.exp || 0} />
                    </button>
                  ))}
                </div>
              )}
              
              {/* Selected User Display */}
              {sendUsername && selectedRecipient && (
                <div className="mt-3 space-y-2">
                  <div className="text-xs text-slate-400">Selected recipient:</div>
                  <div 
                    className="leaderboard-entry bg-slate-800/30 border border-cyan-500/50 rounded-lg p-4 flex justify-between items-center relative"
                    style={selectedRecipient.banner_image ? {
                      backgroundImage: `url(${selectedRecipient.banner_image})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat'
                    } : {}}
                  >
                    {/* Overlay for better text visibility when banner is set */}
                    {selectedRecipient.banner_image && (
                      <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                    )}
                    
                    {/* Level Badge */}
                    <LevelBadge level={selectedRecipient.level || 1} small={true} verified={selectedRecipient.has_made_first_purchase} />
                    
                    <div className="flex items-center gap-3 relative z-10">
                      {/* Profile Picture */}
                      {selectedRecipient.profile_picture ? (
                        <img
                          src={selectedRecipient.profile_picture}
                          alt={selectedRecipient.username}
                          className="w-12 h-12 rounded-full object-cover border-2 flex-shrink-0"
                          style={{ borderColor: selectedRecipient.circle_color || '#475569' }}
                        />
                      ) : (
                        <div 
                          className="w-12 h-12 rounded-full bg-slate-700 border-2 flex items-center justify-center text-slate-400 font-semibold text-sm flex-shrink-0"
                          style={{ borderColor: selectedRecipient.circle_color || '#475569' }}
                        >
                          {selectedRecipient.username.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className={`font-medium ${selectedRecipient.banner_image ? 'text-white drop-shadow-lg' : 'text-slate-200'}`}>
                        {selectedRecipient.username}
                      </span>
                    </div>
                    
                    <span className={`digital-font font-semibold relative z-10 ${selectedRecipient.banner_image ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                      {selectedRecipient.battle_points} BP
                    </span>
                    
                    {/* Trophy System - Top Right */}
                    <div className="absolute top-1 right-2 flex items-center gap-1 z-10">
                      {/* Gold Trophy */}
                      <div className="flex items-center gap-0.5">
                        <Trophy className="w-3 h-3 text-yellow-500" />
                        <span className="text-[10px] font-bold text-white drop-shadow">{selectedRecipient.gold_trophies || 0}</span>
                      </div>
                      {/* Silver Trophy */}
                      <div className="flex items-center gap-0.5">
                        <Trophy className="w-3 h-3 text-slate-300" />
                        <span className="text-[10px] font-bold text-white drop-shadow">{selectedRecipient.silver_trophies || 0}</span>
                      </div>
                      {/* Bronze Trophy */}
                      <div className="flex items-center gap-0.5">
                        <Trophy className="w-3 h-3 text-amber-600" />
                        <span className="text-[10px] font-bold text-white drop-shadow">{selectedRecipient.bronze_trophies || 0}</span>
                      </div>
                    </div>
                    
                    {/* EXP Progress Bar */}
                    <ExpProgressBar level={selectedRecipient.level || 1} exp={selectedRecipient.exp || 0} />
                  </div>
                  
                  <button
                    onClick={() => {
                      setSendUsername('');
                      setSelectedRecipient(null);
                    }}
                    className="text-xs text-red-400 hover:text-red-300"
                  >
                    Clear selection
                  </button>
                </div>
              )}
              
              <div className="text-xs text-slate-500 mt-1">
                Scan recipient's QR code or search by username
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="send-points" className="text-slate-300">
                Battle Points
              </Label>
              <Input
                id="send-points"
                data-testid="send-points-input"
                type="number"
                value={sendPoints}
                onChange={(e) => setSendPoints(e.target.value)}
                placeholder="Enter points"
                min="1"
                className="bg-slate-800 border-slate-700 text-white"
              />
            </div>
            <div className="text-sm text-slate-400">
              Your balance: <span className="text-cyan-400 font-semibold">{user.battle_points} BP</span>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={handleSendPoints}
              data-testid="send-confirm-button"
              disabled={loading}
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
            >
              {loading ? 'Sending...' : 'Send Points'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Receive Points Modal */}
      <Dialog open={showReceiveModal} onOpenChange={setShowReceiveModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white top-[10%] translate-y-0" data-testid="receive-modal">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 digital-font text-xl">RECEIVE BATTLE POINTS</DialogTitle>
            <DialogDescription className="text-slate-400">
              Share your QR code or username to receive points
            </DialogDescription>
            <p className="text-xs text-yellow-400 mt-2">
              Earn Exp by receiving battle points from other bladers to level up and receive unlock tokens! Each unlock token will unlock any beyblade from the store without spending battle points.
            </p>
          </DialogHeader>
          <div className="py-6 space-y-6">
            {/* QR Code */}
            <div className="bg-white rounded-xl p-6 flex justify-center">
              <QRCodeSVG
                value={user.username}
                size={200}
                level="H"
                data-testid="receive-qr-code"
              />
            </div>

            {/* Player Banner */}
            <div className="space-y-2">
              <div className="text-sm text-slate-400 text-center">Your Profile</div>
              <div 
                className="leaderboard-entry bg-slate-800/30 border border-slate-700/50 rounded-lg p-4 flex justify-between items-center relative"
                style={user.banner_image ? {
                  backgroundImage: `url(${user.banner_image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat'
                } : {}}
              >
                {/* Overlay for better text visibility when banner is set */}
                {user.banner_image && (
                  <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                )}
                
                {/* Level Badge */}
                <LevelBadge level={user.level || 1} small={true} verified={accountStatus?.is_permanent} />
                
                <div className="flex items-center gap-3 relative z-10">
                  {/* Profile Picture */}
                  {user.profile_picture ? (
                    <img
                      src={user.profile_picture}
                      alt={user.username}
                      className="w-12 h-12 rounded-full object-cover border-2 flex-shrink-0"
                      style={{ borderColor: user.circle_color || '#475569' }}
                    />
                  ) : (
                    <div 
                      className="w-12 h-12 rounded-full bg-black border-2 flex items-center justify-center text-cyan-400 font-semibold text-sm flex-shrink-0"
                      style={{ borderColor: user.circle_color || '#475569' }}
                    >
                      {user.username.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className={`font-medium ${user.banner_image ? 'text-white drop-shadow-lg' : 'text-slate-200'}`} data-testid="receive-username-display">
                    {user.username}
                  </span>
                </div>
                
                <span className={`digital-font font-semibold relative z-10 ${user.banner_image ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                  {user.battle_points} BP
                </span>
                
                {/* Trophy System - Top Right */}
                <div className="absolute top-1 right-2 flex items-center gap-1 z-10">
                  {/* Gold Trophy */}
                  <div className="flex items-center gap-0.5">
                    <Trophy className="w-3 h-3 text-yellow-500" />
                    <span className="text-[10px] font-bold text-white drop-shadow">{user.trophies?.gold || 0}</span>
                  </div>
                  {/* Silver Trophy */}
                  <div className="flex items-center gap-0.5">
                    <Trophy className="w-3 h-3 text-slate-300" />
                    <span className="text-[10px] font-bold text-white drop-shadow">{user.trophies?.silver || 0}</span>
                  </div>
                  {/* Bronze Trophy */}
                  <div className="flex items-center gap-0.5">
                    <Trophy className="w-3 h-3 text-amber-600" />
                    <span className="text-[10px] font-bold text-white drop-shadow">{user.trophies?.bronze || 0}</span>
                  </div>
                </div>
                
                {/* EXP Progress Bar */}
                <ExpProgressBar level={user.level || 1} exp={user.exp || 0} />
              </div>
              
              <div className="text-xs text-slate-500 text-center mt-2">
                Other bladers can send you points using this username or by scanning your QR code
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowReceiveModal(false)}
              data-testid="receive-close-button"
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Settings Modal */}
      <Dialog open={showSettingsModal} onOpenChange={setShowSettingsModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white top-[10%] translate-y-0">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 digital-font text-xl">SETTINGS</DialogTitle>
            <DialogDescription className="text-slate-400">
              Manage your account settings
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Messages Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex-1">
                <div className="font-semibold text-white mb-1">Messages</div>
                <div className="text-sm text-slate-400">
                  {messagesEnabled ? 'You can receive messages from other bladers' : 'You will not receive any messages'}
                </div>
              </div>
              <button
                onClick={() => handleToggleMessages(!messagesEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  messagesEnabled ? 'bg-cyan-500' : 'bg-slate-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    messagesEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Notifications Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex-1">
                <div className="font-semibold text-white mb-1">Notifications</div>
                <div className="text-sm text-slate-400">
                  {notificationsEnabled ? 'Receive sound and popup alerts for new messages' : 'No notification alerts for messages'}
                </div>
              </div>
              <button
                onClick={() => handleToggleNotifications(!notificationsEnabled)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  notificationsEnabled ? 'bg-cyan-500' : 'bg-slate-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    notificationsEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Friends Only Messages Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex-1">
                <div className="font-semibold text-white mb-1">Messages from Mutual Friends Only</div>
                <div className="text-sm text-slate-400">
                  {friendsOnlyMessages ? 'Only mutual friends can message you' : 'Anyone can message you'}
                </div>
              </div>
              <button
                onClick={() => handleToggleFriendsOnlyMessages(!friendsOnlyMessages)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  friendsOnlyMessages ? 'bg-purple-500' : 'bg-slate-600'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    friendsOnlyMessages ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Test Notification Button */}
            <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
              <div className="flex items-center justify-between mb-2">
                <div className="flex-1">
                  <div className="font-semibold text-white mb-1">Test Notifications</div>
                  <div className="text-sm text-slate-400 space-y-1">
                    <div>Permission: {checkNotificationPermission() === 'granted' ? '✅ Enabled' : checkNotificationPermission() === 'denied' ? '❌ Blocked' : '⚠️ Not Set'}</div>
                    <div className="text-xs">
                      {checkNotificationPermission() === 'granted' 
                        ? '📱 Badges will update even when app is closed' 
                        : '⚠️ Enable notifications to get badges when app is closed'}
                    </div>
                  </div>
                </div>
                <Button
                  onClick={async () => {
                    console.log('=== NOTIFICATION DIAGNOSTIC ===');
                    const permission = checkNotificationPermission();
                    console.log('1. Permission:', permission);
                    console.log('2. Service Worker support:', 'serviceWorker' in navigator);
                    console.log('3. Push Manager support:', 'PushManager' in window);
                    
                    // Check service worker status
                    if ('serviceWorker' in navigator) {
                      const registration = await navigator.serviceWorker.ready;
                      console.log('4. Service Worker registered:', !!registration);
                      
                      // Check push subscription
                      const subscription = await registration.pushManager.getSubscription();
                      console.log('5. Push subscription exists:', !!subscription);
                      if (subscription) {
                        console.log('6. Subscription endpoint:', subscription.endpoint.substring(0, 50) + '...');
                      }
                    }
                    console.log('=== END DIAGNOSTIC ===');
                    
                    if (permission === 'denied') {
                      toast.error('Notifications are blocked! See instructions below to enable them.');
                      return;
                    }
                    
                    const granted = await requestNotificationPermission();
                    if (granted) {
                      await testNotification();
                      toast.success('Test notification sent! Check your device.');
                    } else {
                      toast.error('Permission denied. Follow the instructions below.');
                    }
                  }}
                  className="bg-blue-500 hover:bg-blue-600"
                >
                  Test
                </Button>
              </div>
              
              {/* Instructions if blocked */}
              {checkNotificationPermission() === 'denied' && (
                <div className="mt-3 p-3 bg-red-500/10 border border-red-500/30 rounded text-xs text-slate-300">
                  <div className="font-semibold text-red-400 mb-2">🔒 Notifications Blocked - How to Enable:</div>
                  <div className="space-y-1">
                    <div><strong>Desktop Chrome/Edge:</strong></div>
                    <div className="pl-2">1. Click the 🔒 lock icon in address bar</div>
                    <div className="pl-2">2. Find "Notifications" → Change to "Allow"</div>
                    <div className="pl-2">3. Refresh the page</div>
                    
                    <div className="mt-2"><strong>Mobile Chrome:</strong></div>
                    <div className="pl-2">1. Tap ⋮ menu → Site settings</div>
                    <div className="pl-2">2. Notifications → Allow</div>
                    <div className="pl-2">3. Return to app</div>
                    
                    <div className="mt-2"><strong>iOS Safari:</strong></div>
                    <div className="pl-2">1. Must add to Home Screen first</div>
                    <div className="pl-2">2. Open from Home Screen (not browser)</div>
                    <div className="pl-2">3. Grant permission when prompted</div>
                  </div>
                </div>
              )}
              
              {checkNotificationPermission() === 'granted' && (
                <div className="mt-3 p-3 bg-green-500/10 border border-green-500/30 rounded text-xs text-green-400">
                  ✓ Notifications enabled! You'll receive alerts for new messages.
                </div>
              )}
            </div>

{/* Admin Backup Button - Hidden from regular users */}
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowSettingsModal(false)}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profile Picture Upload Modal */}
      <Dialog open={showProfileModal} onOpenChange={setShowProfileModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white top-[10%] translate-y-0">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 digital-font text-xl">PROFILE PICTURE</DialogTitle>
            <DialogDescription className="text-slate-400">
              Upload a profile picture to personalize your account
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {/* Account Status Section */}
            <div className="bg-slate-800 rounded-lg p-3 border border-slate-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {accountStatus?.is_permanent ? (
                    <>
                      <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                      <span className="text-blue-400 font-semibold text-sm">Permanent Account</span>
                      <svg className="w-4 h-4 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                    </>
                  ) : (
                    <>
                      <div className="w-3 h-3 bg-orange-500 rounded-full animate-pulse"></div>
                      <span className="text-orange-400 font-semibold text-sm">Temporary Account</span>
                    </>
                  )}
                </div>
                {!accountStatus?.is_permanent && (
                  <Button
                    size="sm"
                    onClick={() => setShowVerifyAccountModal(true)}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1 h-7"
                  >
                    Verify Account
                  </Button>
                )}
              </div>
              {!accountStatus?.is_permanent && accountStatus?.time_remaining_formatted && (
                <div className="mt-2 text-xs text-slate-400">
                  <span className="text-orange-400 font-mono">{accountStatus.time_remaining_formatted}</span> remaining before account deletion
                </div>
              )}
            </div>

            {/* Circle Color Button - Top Left, Delete Button - Top Right */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <input
                  type="file"
                  id="circle-color-input"
                  accept="image/*"
                  onChange={handleCircleColorUpload}
                  className="hidden"
                  disabled={uploadingCircleColor}
                />
                <label htmlFor="circle-color-input">
                  <Button
                    type="button"
                    size="sm"
                    disabled={uploadingCircleColor}
                    className="bg-cyan-400 hover:bg-cyan-500 text-slate-900 text-xs px-3 py-1 h-7 cursor-pointer"
                    onClick={() => document.getElementById('circle-color-input').click()}
                  >
                    {uploadingCircleColor ? 'Extracting...' : 'Circle Color'}
                  </Button>
                </label>
                
                {user.circle_color && (
                  <>
                    <div 
                      className="w-5 h-5 rounded-full border-2" 
                      style={{ backgroundColor: user.circle_color, borderColor: user.circle_color }}
                    />
                    <Button
                      onClick={handleResetCircleColor}
                      variant="ghost"
                      size="sm"
                      className="text-slate-500 hover:text-slate-300 text-xs px-2 py-1 h-7"
                    >
                      Reset
                    </Button>
                  </>
                )}
              </div>

              {/* Delete Account Button - Far Right */}
              <button
                onMouseDown={handleDeleteHoldStart}
                onMouseUp={handleDeleteHoldEnd}
                onMouseLeave={handleDeleteHoldEnd}
                onTouchStart={handleDeleteHoldStart}
                onTouchEnd={handleDeleteHoldEnd}
                onContextMenu={(e) => e.preventDefault()}
                className="h-7 rounded border border-red-500/50 bg-slate-800 relative overflow-hidden transition-all hover:border-red-500 text-xs select-none"
                title="Hold for 5 seconds to delete account"
                style={{ 
                  width: '85px',
                  WebkitUserSelect: 'none',
                  WebkitTouchCallout: 'none',
                  userSelect: 'none'
                }}
              >
                {/* Red fill progress from left to right */}
                <div 
                  className="absolute inset-0 bg-gradient-to-r from-red-600 to-red-500 transition-all duration-100"
                  style={{ width: `${deleteHoldProgress}%` }}
                />
                <span className={`relative z-10 font-semibold ${deleteHoldProgress > 50 ? 'text-white' : 'text-red-400'}`}>
                  Delete
                </span>
              </button>
            </div>

            {/* Current Profile Picture */}
            <div className="flex justify-center">
              {user.profile_picture ? (
                <img
                  src={user.profile_picture}
                  alt={user.username}
                  className="w-32 h-32 rounded-full object-cover border-4"
                  style={{ borderColor: user.circle_color || '#475569' }}
                />
              ) : (
                <div 
                  className="w-32 h-32 rounded-full bg-slate-700 border-4 flex items-center justify-center text-slate-400 font-semibold text-4xl"
                  style={{ borderColor: user.circle_color || '#475569' }}
                >
                  {user.username.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            {/* Upload Button */}
            <div className="flex flex-col items-center gap-3">
              <input
                type="file"
                id="profile-picture-input"
                accept="image/jpeg,image/png,image/webp,image/jpg"
                onChange={handleProfilePictureUpload}
                className="hidden"
                disabled={uploadingPicture}
              />
              <label htmlFor="profile-picture-input">
                <Button
                  type="button"
                  disabled={uploadingPicture}
                  className="bg-gradient-to-r from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white cursor-pointer"
                  onClick={() => document.getElementById('profile-picture-input').click()}
                >
                  {uploadingPicture ? 'Uploading...' : 'Choose Picture'}
                </Button>
              </label>
              <p className="text-xs text-slate-500 text-center">
                Max size: 5MB • Formats: JPG, PNG, WEBP
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowProfileModal(false)}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Account Confirmation Modal */}
      <Dialog open={showDeleteAccountModal} onOpenChange={(open) => {
        setShowDeleteAccountModal(open);
        if (!open) {
          setDeletePassword('');
        }
      }}>
        <DialogContent className="bg-slate-900 border-red-500/50 text-white top-[10%] translate-y-0 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-500 font-bold text-lg flex items-center justify-center gap-2">
              <span className="text-2xl">❗</span>
              Delete this account?
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-red-900/20 rounded-lg p-4 border border-red-500/30">
              <p className="text-slate-300 text-sm leading-relaxed">
                <span className="text-red-400 font-semibold">All data will be deleted. This can't be undone.</span>
              </p>
              <p className="text-slate-400 text-sm mt-2">
                Enter your password to permanently delete this account.
              </p>
            </div>
            
            <div className="space-y-2">
              <label className="text-sm text-slate-400">Password</label>
              <input
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Enter your password"
                className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:border-red-500 focus:outline-none"
                disabled={deletingAccount}
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2">
            <Button
              onClick={() => {
                setShowDeleteAccountModal(false);
                setDeletePassword('');
              }}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              disabled={deletingAccount}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDeleteAccount}
              disabled={deletingAccount || !deletePassword.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deletingAccount ? 'Deleting...' : `Delete User: ${user.username}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Verify Account Modal */}
      <Dialog open={showVerifyAccountModal} onOpenChange={setShowVerifyAccountModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white top-[10%] translate-y-0">
          <DialogHeader>
            <DialogTitle className="text-blue-400 digital-font text-xl flex items-center gap-2">
              <svg className="w-6 h-6 text-blue-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              VERIFY YOUR ACCOUNT
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-slate-800 rounded-lg p-4 border border-orange-500/30">
              <p className="text-slate-300 text-sm leading-relaxed">
                In order to reduce strain on the database, <span className="text-orange-400 font-semibold">temporary accounts will be deleted every 14 days</span>. If not logged in or left inactive.
              </p>
              <p className="text-slate-400 text-sm mt-3 leading-relaxed">
                If your account is deleted, don't worry! You can re-register an account with the same username at any time if that username is available.
              </p>
            </div>
            
            <div className="bg-blue-900/30 rounded-lg p-4 border border-blue-500/30">
              <h4 className="text-blue-400 font-semibold text-sm mb-2">How to make your account permanent:</h4>
              <p className="text-slate-300 text-sm">
                Purchase your first <span className="text-cyan-400 font-bold">100 Battle Points</span> to unlock the Daily Gift feature and make your account permanent. Your account will never be deleted!
              </p>
            </div>

            {accountStatus && !accountStatus.is_permanent && (
              <div className="text-center py-2">
                <span className="text-slate-400 text-xs">Time remaining: </span>
                <span className="text-orange-400 font-mono font-bold">{accountStatus.time_remaining_formatted}</span>
              </div>
            )}
          </div>
          <DialogFooter className="flex flex-col sm:flex-col gap-2">
            <Button
              onClick={() => {
                setShowVerifyAccountModal(false);
                setShowProfileModal(false);
                // Scroll to purchase button or trigger purchase
                handlePurchase();
              }}
              className="w-full bg-cyan-600 hover:bg-cyan-700 text-white"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              Purchase 100BP   $1
            </Button>
            <Button
              onClick={() => setShowVerifyAccountModal(false)}
              variant="outline"
              className="w-full border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Device Background Upload Modal */}
      <Dialog open={showDeviceBackgroundModal} onOpenChange={setShowDeviceBackgroundModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white top-[10%] translate-y-0">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 digital-font text-xl">DEVICE BACKGROUND</DialogTitle>
            <DialogDescription className="text-slate-400">
              Customize your Beypointer device with a background image
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Preview */}
            {user.device_background && (
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Current Background</label>
                <div className="relative w-full h-40 rounded-lg overflow-hidden border-2 border-slate-700">
                  <img
                    src={user.device_background}
                    alt="Device background"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}

            {/* Upload Button */}
            <div>
              <label className="text-sm text-slate-400 mb-2 block">
                {user.device_background ? 'Change Background' : 'Upload Background'}
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleDeviceBackgroundUpload}
                disabled={uploadingDeviceBackground}
                className="block w-full text-sm text-slate-400
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:bg-cyan-500 file:text-white
                  hover:file:bg-cyan-600
                  file:cursor-pointer cursor-pointer
                  disabled:opacity-50"
              />
              <p className="text-xs text-slate-500 mt-2">
                Recommended: JPG, PNG, or WEBP. Max size: 5MB
              </p>
            </div>

            {/* Remove Button */}
            {user.device_background && (
              <Button
                onClick={handleRemoveDeviceBackground}
                variant="outline"
                className="w-full border-red-500 text-red-400 hover:bg-red-500/10"
              >
                Remove Background
              </Button>
            )}

            {uploadingDeviceBackground && (
              <div className="text-center text-sm text-cyan-400 animate-pulse">
                Uploading...
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowDeviceBackgroundModal(false)}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Outer Device Background Upload Modal */}
      <Dialog open={showOuterDeviceBackgroundModal} onOpenChange={setShowOuterDeviceBackgroundModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white top-[10%] translate-y-0">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 digital-font text-xl">DEVICE FRAME</DialogTitle>
            <DialogDescription className="text-slate-400">
              Customize the outer frame area of your Beypointer device (buttons stay unchanged)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Preview */}
            {user.outer_device_background && (
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Current Frame Background</label>
                <div className="relative w-full h-40 rounded-lg overflow-hidden border-2 border-slate-700">
                  <img
                    src={user.outer_device_background}
                    alt="Outer device background"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}

            {/* Upload Button */}
            <div>
              <label className="text-sm text-slate-400 mb-2 block">
                {user.outer_device_background ? 'Change Frame Background' : 'Upload Frame Background'}
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleOuterDeviceBackgroundUpload}
                disabled={uploadingOuterDeviceBackground}
                className="block w-full text-sm text-slate-400
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:bg-cyan-500 file:text-white
                  hover:file:bg-cyan-600
                  file:cursor-pointer cursor-pointer
                  disabled:opacity-50"
              />
              <p className="text-xs text-slate-500 mt-2">
                Recommended: JPG, PNG, or WEBP. Max size: 5MB
              </p>
            </div>

            {/* Remove Button */}
            {user.outer_device_background && (
              <Button
                onClick={handleRemoveOuterDeviceBackground}
                variant="outline"
                className="w-full border-red-500 text-red-400 hover:bg-red-500/10"
              >
                Remove Frame Background
              </Button>
            )}

            {uploadingOuterDeviceBackground && (
              <div className="text-center text-sm text-cyan-400 animate-pulse">
                Uploading...
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowOuterDeviceBackgroundModal(false)}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leaderboard Background Upload Modal */}
      <Dialog open={showLeaderboardBackgroundModal} onOpenChange={setShowLeaderboardBackgroundModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white top-[10%] translate-y-0">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 digital-font text-xl">LEADERBOARD BACKGROUND</DialogTitle>
            <DialogDescription className="text-slate-400">
              Customize the leaderboard section with a background image
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Preview */}
            {user.leaderboard_background && (
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Current Leaderboard Background</label>
                <div className="relative w-full h-40 rounded-lg overflow-hidden border-2 border-slate-700">
                  <img
                    src={user.leaderboard_background}
                    alt="Leaderboard background"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}

            {/* Upload Button */}
            <div>
              <label className="text-sm text-slate-400 mb-2 block">
                {user.leaderboard_background ? 'Change Leaderboard Background' : 'Upload Leaderboard Background'}
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleLeaderboardBackgroundUpload}
                disabled={uploadingLeaderboardBackground}
                className="block w-full text-sm text-slate-400
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:bg-cyan-500 file:text-white
                  hover:file:bg-cyan-600
                  file:cursor-pointer cursor-pointer
                  disabled:opacity-50"
              />
              <p className="text-xs text-slate-500 mt-2">
                Recommended: JPG, PNG, or WEBP. Max size: 5MB
              </p>
            </div>

            {/* Remove Button */}
            {user.leaderboard_background && (
              <Button
                onClick={handleRemoveLeaderboardBackground}
                variant="outline"
                className="w-full border-red-500 text-red-400 hover:bg-red-500/10"
              >
                Remove Leaderboard Background
              </Button>
            )}

            {uploadingLeaderboardBackground && (
              <div className="text-center text-sm text-cyan-400 animate-pulse">
                Uploading...
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowLeaderboardBackgroundModal(false)}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Banner Upload Modal */}
      <Dialog open={showBannerModal} onOpenChange={setShowBannerModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white top-[10%] translate-y-0">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 digital-font text-xl">LEADERBOARD BANNER</DialogTitle>
            <DialogDescription className="text-slate-400">
              Customize your banner that appears in the leaderboard and search results
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Preview */}
            {user.banner_image && (
              <div className="space-y-2">
                <label className="text-sm text-slate-400">Current Banner</label>
                <div className="relative w-full h-20 rounded-lg overflow-hidden border-2 border-slate-700">
                  <img
                    src={user.banner_image}
                    alt="Banner"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}

            {/* Upload Button */}
            <div>
              <label className="text-sm text-slate-400 mb-2 block">
                {user.banner_image ? 'Change Banner' : 'Upload Banner'}
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleBannerUpload}
                disabled={uploadingBanner}
                className="block w-full text-sm text-slate-400
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:bg-cyan-500 file:text-white
                  hover:file:bg-cyan-600
                  file:cursor-pointer cursor-pointer
                  disabled:opacity-50"
              />
              <p className="text-xs text-slate-500 mt-2">
                Recommended: Wide landscape image (e.g., 800x200px). Max size: 5MB
              </p>
            </div>

            {/* Remove Button */}
            {user.banner_image && (
              <Button
                onClick={handleRemoveBanner}
                variant="outline"
                className="w-full border-red-500 text-red-400 hover:bg-red-500/10"
              >
                Remove Banner
              </Button>
            )}

            {uploadingBanner && (
              <div className="text-center text-sm text-cyan-400 animate-pulse">
                Uploading...
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={() => setShowBannerModal(false)}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* QR Scanner */}
      {showQRScanner && (
        <QRScanner
          onScan={handleQRScan}
          onClose={() => setShowQRScanner(false)}
        />
      )}

      {/* Notification Audio */}
      <audio ref={notificationAudioRef} src="/message-notification.mp3" preload="auto" />

      {/* Global Notification Popup */}
      {notification && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-slide-down">
          <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-2xl p-4 min-w-[320px] max-w-md">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <MessageCircle className="w-8 h-8 text-cyan-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-white mb-1">{notification.sender}</div>
                <div className="text-sm text-slate-300 truncate">{notification.message}</div>
              </div>
              <button
                onClick={() => setNotification(null)}
                className="flex-shrink-0 text-slate-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Permission Prompt Modal */}
      {/* Notification Permission Prompt */}
      <Dialog open={showNotificationPrompt} onOpenChange={setShowNotificationPrompt}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 text-xl">🔔 Enable Notifications?</DialogTitle>
            <DialogDescription className="text-slate-300 text-base">
              Stay updated with real-time message alerts even when the app is in the background!
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex items-start gap-3">
                <div className="text-2xl">💬</div>
                <div>
                  <div className="font-semibold text-white">Instant Message Alerts</div>
                  <div className="text-sm text-slate-400">Get notified when someone messages you</div>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="text-2xl">🔊</div>
                <div>
                  <div className="font-semibold text-white">Sound & Vibration</div>
                  <div className="text-sm text-slate-400">Hear and feel alerts when messages arrive</div>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="text-2xl">📱</div>
                <div>
                  <div className="font-semibold text-white">Background Alerts</div>
                  <div className="text-sm text-slate-400">Receive alerts even when app is closed</div>
                </div>
              </div>
            </div>
            
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-3">
              <div className="text-sm text-cyan-300 font-semibold mb-1">
                🎯 Recommended for Best Experience
              </div>
              <div className="text-xs text-slate-400">
                You can disable this anytime in Settings
              </div>
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button
              onClick={() => {
                setShowNotificationPrompt(false);
                toast.info('You can enable notifications later in Settings');
              }}
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
            >
              Not Now
            </Button>
            <Button
              onClick={async () => {
                // Check if already denied
                if (Notification.permission === 'denied') {
                  setShowNotificationPrompt(false);
                  setShowNotificationBlockedModal(true);
                  return;
                }
                
                const granted = await requestNotificationPermission();
                if (granted) {
                  await testNotification();
                  toast.success('🎉 Notifications enabled! You\'ll receive alerts for new messages.');
                  setNotificationsEnabled(true);
                } else {
                  // Permission was denied
                  setShowNotificationBlockedModal(true);
                }
                setShowNotificationPrompt(false);
              }}
              className="bg-cyan-500 hover:bg-cyan-600"
            >
              Enable Notifications
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Notification Blocked Instructions Modal */}
      <Dialog open={showNotificationBlockedModal} onOpenChange={setShowNotificationBlockedModal}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-red-400 text-xl">🚫 Notifications Blocked</DialogTitle>
            <DialogDescription className="text-slate-300 text-base">
              Notifications are currently blocked by your browser. Follow the steps below to enable them manually.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
            {/* Chrome/Edge Instructions */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="text-lg font-semibold text-cyan-400 mb-3">📱 Chrome / Edge (Mobile & Desktop)</div>
              <ol className="list-decimal list-inside space-y-2 text-sm text-slate-300">
                <li>Tap the <strong className="text-white">lock icon (🔒)</strong> or <strong className="text-white">info icon (ⓘ)</strong> in the address bar</li>
                <li>Find <strong className="text-white">"Notifications"</strong> or <strong className="text-white">"Permissions"</strong></li>
                <li>Change setting from <span className="text-red-400">"Block"</span> to <span className="text-cyan-400">"Allow"</span></li>
                <li>Refresh this page</li>
              </ol>
            </div>

            {/* Safari Instructions */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="text-lg font-semibold text-cyan-400 mb-3">🍎 Safari (iOS/Mac)</div>
              <ol className="list-decimal list-inside space-y-2 text-sm text-slate-300">
                <li>Tap <strong className="text-white">Safari</strong> in address bar or go to <strong className="text-white">Settings</strong></li>
                <li>Scroll to <strong className="text-white">Website Settings</strong></li>
                <li>Tap <strong className="text-white">Notifications</strong></li>
                <li>Find this website and set to <span className="text-cyan-400">"Allow"</span></li>
                <li>Return to app and refresh</li>
              </ol>
            </div>

            {/* Firefox Instructions */}
            <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4">
              <div className="text-lg font-semibold text-cyan-400 mb-3">🦊 Firefox</div>
              <ol className="list-decimal list-inside space-y-2 text-sm text-slate-300">
                <li>Click the <strong className="text-white">lock icon (🔒)</strong> in the address bar</li>
                <li>Click <strong className="text-white">"Connection secure"</strong> or <strong className="text-white">"More information"</strong></li>
                <li>Go to <strong className="text-white">"Permissions"</strong> tab</li>
                <li>Find <strong className="text-white">"Notifications"</strong> and uncheck <span className="text-red-400">"Blocked"</span></li>
                <li>Close and refresh the page</li>
              </ol>
            </div>

            {/* Alternative Method */}
            <div className="bg-cyan-500/10 border border-cyan-500/30 rounded-lg p-4">
              <div className="text-sm font-semibold text-cyan-300 mb-2">
                💡 Alternative: Browser Settings
              </div>
              <div className="text-xs text-slate-400 space-y-1">
                <p>1. Open your browser <strong>Settings</strong></p>
                <p>2. Search for <strong>"Notifications"</strong> or <strong>"Site Permissions"</strong></p>
                <p>3. Find this website in the blocked list</p>
                <p>4. Change to <strong>"Allow"</strong></p>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => setShowNotificationBlockedModal(false)}
              className="bg-cyan-500 hover:bg-cyan-600 w-full"
            >
              Got It
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Online Users Modal */}
      <Dialog open={showOnlineUsersModal} onOpenChange={(open) => {
        setShowOnlineUsersModal(open);
        if (!open) setOnlineUserSearchQuery(''); // Clear search when closing
      }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-2xl max-h-[80vh] overflow-y-auto !top-[10%] translate-y-0">
          <DialogHeader>
            <DialogTitle className="text-cyan-400 digital-font text-xl">ONLINE BLADERS</DialogTitle>
            <DialogDescription className="text-slate-400">
              {onlineUsers.length} {onlineUsers.length === 1 ? 'blader' : 'bladers'} currently online
            </DialogDescription>
          </DialogHeader>
          
          {/* Search Input */}
          <div className="py-2">
            <Input
              type="text"
              placeholder="Search online users..."
              value={onlineUserSearchQuery}
              onChange={(e) => setOnlineUserSearchQuery(e.target.value)}
              className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-400"
            />
          </div>
          
          <div className="space-y-2 py-2">
            {(() => {
              // Filter online users based on search query
              const filteredUsers = onlineUsers.filter(player => 
                player.username.toLowerCase().includes(onlineUserSearchQuery.toLowerCase())
              );
              
              if (filteredUsers.length === 0) {
                return (
                  <p className="text-slate-400 text-center py-8">
                    {onlineUserSearchQuery ? 'No users found matching your search' : 'Loading please wait...'}
                  </p>
                );
              }
              
              return filteredUsers.map((player, index) => (
                <div
                  key={player.id}
                  className="leaderboard-entry bg-slate-800/30 border border-slate-700/50 rounded-lg p-3 flex justify-between items-center relative"
                  style={player.banner_image ? {
                    backgroundImage: `url(${player.banner_image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  } : {}}
                >
                  {/* Overlay for better text visibility when banner is set */}
                  {player.banner_image && (
                    <div className="absolute inset-0 bg-black/40 rounded-lg"></div>
                  )}
                  
                  {/* Level Badge */}
                  <LevelBadge level={player.level || 1} small={true} verified={player.has_made_first_purchase} />
                  
                  <div className="flex items-center gap-3 relative z-10">
                    {/* Rank Number */}
                    <div
                      className={`text-xl font-bold min-w-[32px] text-center ${
                        index === 0
                          ? 'text-yellow-500'
                          : index === 1
                          ? 'text-slate-300'
                          : index === 2
                          ? 'text-amber-600'
                          : 'text-slate-400'
                      }`}
                    >
                      {index + 1}
                    </div>
                    
                    {/* Profile Picture */}
                    {player.profile_picture ? (
                      <img
                        src={player.profile_picture}
                        alt={player.username}
                        className="w-12 h-12 rounded-full object-cover border-2 pointer-events-none"
                        style={{ borderColor: player.circle_color || '#475569' }}
                      />
                    ) : (
                      <div 
                        className="w-12 h-12 rounded-full bg-slate-700 border-2 flex items-center justify-center text-slate-300 text-sm font-semibold pointer-events-none"
                        style={{ borderColor: player.circle_color || '#475569' }}
                      >
                        {player.username.charAt(0).toUpperCase()}
                      </div>
                    )}
                    
                    <span className={`font-medium ${player.banner_image ? 'text-white drop-shadow-lg' : 'text-slate-200'}`}>
                      {player.username}
                    </span>
                  </div>
                  
                  <span className={`digital-font font-semibold relative z-10 ${player.banner_image ? 'text-white drop-shadow-lg' : 'text-cyan-400'}`}>
                    {player.battle_points} BP
                  </span>
                  
                  {/* Trophy System - Top Right */}
                  <div className="absolute top-1 right-2 flex items-center gap-1 z-10">
                    <div className="flex items-center gap-0.5">
                      <Trophy className="w-3 h-3 text-yellow-500" />
                      <span className="text-[10px] font-bold text-white drop-shadow">{player.gold_trophies || 0}</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <Trophy className="w-3 h-3 text-slate-300" />
                      <span className="text-[10px] font-bold text-white drop-shadow">{player.silver_trophies || 0}</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <Trophy className="w-3 h-3 text-amber-600" />
                      <span className="text-[10px] font-bold text-white drop-shadow">{player.bronze_trophies || 0}</span>
                    </div>
                  </div>
                  
                  {/* EXP Progress Bar */}
                  <ExpProgressBar level={player.level || 1} exp={player.exp || 0} />
                </div>
              ));
            })()}
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => setShowOnlineUsersModal(false)}
              className="bg-cyan-500 hover:bg-cyan-600 w-full"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Battle Stadium Popup - Shows once per login */}
      {showBattlePopup && (
        <div 
          className="fixed inset-0 flex items-center justify-center z-50 p-4 pointer-events-none"
          style={{ paddingTop: 'clamp(80px, 12vh, 140px)' }}
        >
          <div 
            className="relative border-2 border-red-500 rounded-lg overflow-hidden shadow-2xl animate-scale-in pointer-events-auto"
            style={{ 
              width: 'min(320px, 85vw)',
              height: 'min(380px, 50vh)',
              backgroundImage: user.battle_stadium_background 
                ? `url(${user.battle_stadium_background})` 
                : 'url(https://customer-assets.emergentagent.com/job_0ddfa8dd-f7f0-44b2-a1dd-341ae5dc7366/artifacts/6tz3u9fb_IMG_1461.jpeg)',
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}
          >
            {/* Close button */}
            <button
              onClick={() => {
                setShowBattlePopup(false);
                popupDismissedRef.current = true;
              }}
              className="absolute top-2 right-2 text-slate-400 hover:text-white transition-colors z-50 bg-slate-900/80 rounded-full p-1"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Battle Stadium Preview - Clickable to navigate */}
            <div 
              className="cursor-pointer w-full h-full flex items-center justify-center overflow-hidden relative z-10"
              onClick={() => {
                setShowBattlePopup(false);
                popupDismissedRef.current = true;
                onOpenBattle();
              }}
            >
              {/* Actual game preview - scales with popup */}
              <div 
                style={{ 
                  transform: 'scale(0.45)',
                  transformOrigin: 'center center',
                  width: '222%',
                  height: '222%'
                }}
              >
                {/* Header */}
                <div className="flex items-center justify-center p-4 border-b border-slate-700/50">
                  <h1 className="text-2xl font-bold text-red-400 digital-font text-center drop-shadow-lg">
                    BATTLE ARENA
                  </h1>
                </div>

                {/* Battle Stadium Content */}
                <div className="-mt-[16px] pointer-events-none">
                  <InteractiveMap 
                    user={user} 
                    nearbyPlayers={[]}
                    onPlayerClick={() => {}}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Admin Backup Modal */}
      {showAdminBackup && (
        <div className="fixed inset-0 z-50">
          <AdminBackup
            onBack={() => setShowAdminBackup(false)}
          />
        </div>
      )}
    </div>
  );
};

export default Dashboard;
